import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiBell, FiBellOff } from 'react-icons/fi';
import api from '../services/api';
import Modal from '../components/Modal';
import '../components/Button.css';
import './Tickets.css';
import {
  isTicketSoundEnabled,
  setTicketSoundEnabled,
  playNewTicketSound,
} from '../services/ticketSound';

const CATEGORY_COLORS = {
  levering: '#2563eb',
  product: '#7c3aed',
  retour: '#ea580c',
  dealer: '#16a34a',
  klacht: '#dc2626',
  overig: '#6b7280',
};

function Badge({ category }) {
  const c = CATEGORY_COLORS[category] || CATEGORY_COLORS.overig;
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: '0.7rem',
        padding: '0.15rem 0.45rem',
        borderRadius: 6,
        background: `${c}22`,
        color: c,
        fontWeight: 600,
      }}
    >
      {category}
    </span>
  );
}

function PriorityDot({ priority }) {
  if (priority === 'hoog') {
    return <span title="Hoog" style={{ color: '#dc2626', marginLeft: 6 }}>●</span>;
  }
  if (priority === 'laag') {
    return <span title="Laag" style={{ color: '#9ca3af', marginLeft: 6 }}>●</span>;
  }
  return <span title="Middel" style={{ color: '#f59e0b', marginLeft: 6 }}>●</span>;
}

function ticketsOpenQueueCount(counts) {
  return (Number(counts?.open) || 0) + (Number(counts?.inbehandeling) || 0);
}

function getTicketsKlaarStorageKey() {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function markTicketsHadOpenQueue() {
  try {
    sessionStorage.setItem(`tickets-had-open-${getTicketsKlaarStorageKey()}`, '1');
  } catch (_) {
    /* noop */
  }
}

function markTicketsInboxZeroToday() {
  try {
    sessionStorage.setItem(`tickets-inbox-zero-${getTicketsKlaarStorageKey()}`, '1');
  } catch (_) {
    /* noop */
  }
}

function hadTicketsOpenQueueToday() {
  try {
    return sessionStorage.getItem(`tickets-had-open-${getTicketsKlaarStorageKey()}`) === '1';
  } catch (_) {
    return false;
  }
}

function hadTicketsInboxZeroToday() {
  try {
    return sessionStorage.getItem(`tickets-inbox-zero-${getTicketsKlaarStorageKey()}`) === '1';
  } catch (_) {
    return false;
  }
}

function isTicketsKlaarBannerDismissedToday() {
  try {
    return sessionStorage.getItem(`tickets-klaar-dismissed-${getTicketsKlaarStorageKey()}`) === '1';
  } catch (_) {
    return false;
  }
}

function clearTicketsKlaarBannerDismissedToday() {
  try {
    sessionStorage.removeItem(`tickets-klaar-dismissed-${getTicketsKlaarStorageKey()}`);
  } catch (_) {
    /* noop */
  }
}

function dismissTicketsKlaarBannerForToday() {
  try {
    sessionStorage.setItem(`tickets-klaar-dismissed-${getTicketsKlaarStorageKey()}`, '1');
  } catch (_) {
    /* noop */
  }
}

function isTicketsQueueKlaar(counts) {
  return ticketsOpenQueueCount(counts) === 0;
}

function isTicketsQueueKlaarForCelebration(counts) {
  if (!isTicketsQueueKlaar(counts)) return false;
  const af = Number(counts?.afgehandeld) || 0;
  const alle = Number(counts?.alle) || 0;
  return af > 0 || alle > 0 || hadTicketsOpenQueueToday() || hadTicketsInboxZeroToday();
}

function parseTicketsStatusCounts(data) {
  const d = data || {};
  return {
    open: Number(d.open) || 0,
    inbehandeling: Number(d.inbehandeling) || 0,
    afgehandeld: Number(d.afgehandeld) || 0,
    alle: Number(d.alle) || 0,
    mine: Number(d.mine) || 0,
  };
}

function shouldShowTicketsKlaarCelebration(counts) {
  return isTicketsQueueKlaarForCelebration(counts);
}

function wasTicketsKlaarModalShownToday() {
  try {
    return sessionStorage.getItem(`tickets-klaar-modal-${getTicketsKlaarStorageKey()}`) === '1';
  } catch (_) {
    return false;
  }
}

function markTicketsKlaarModalShownToday() {
  try {
    sessionStorage.setItem(`tickets-klaar-modal-${getTicketsKlaarStorageKey()}`, '1');
  } catch (_) {
    /* noop */
  }
}

function buildTicketsKlaarSubtekst(counts) {
  const af = Number(counts?.afgehandeld) || 0;
  if (af <= 0) return null;
  return `${af} ticket${af === 1 ? '' : 's'} staan op afgehandeld`;
}

const TICKETS_KLAAR_MELDINGEN = [
  {
    title: 'Je bent waanzinnig!!',
    body: 'Alle tickets afgehandeld. Supergoed bezig! Tijd voor een lekkere bak koffie.',
    emoji: '☕',
  },
  {
    title: 'Inbox zero!',
    body: 'Alle tickets zijn afgehandeld. Tijd voor een bak koffie.',
    emoji: '🎉',
  },
  {
    title: 'Goed gewerkt, topper!',
    body: 'Geen open tickets meer. Geniet van je welverdiende pauze.',
    emoji: '💪',
  },
  {
    title: 'Alles bijgewerkt!',
    body: 'De ticketwachtrij is leeg. Sterk teamwerk — tijd voor koffie.',
    emoji: '☕',
  },
];

function pickTicketsKlaarMelding() {
  const idx = Math.floor(Math.random() * TICKETS_KLAAR_MELDINGEN.length);
  return TICKETS_KLAAR_MELDINGEN[idx];
}

function buildTicketsKlaarMeldingPayload(counts, extra = {}) {
  return {
    ...pickTicketsKlaarMelding(),
    subtekst: buildTicketsKlaarSubtekst(counts),
    ...extra,
  };
}

function MailboxTag({ address, aliases }) {
  if (!address) return null;
  const display = (aliases && aliases[address]) || address;
  return (
    <span
      title={display !== address ? `Postvak: ${display} (intern: ${address})` : 'Postvak'}
      style={{
        fontSize: '0.65rem',
        fontWeight: 600,
        color: '#0369a1',
        background: '#e0f2fe',
        padding: '0.1rem 0.35rem',
        borderRadius: 4,
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {display}
    </span>
  );
}

function CountBadge({ count, active }) {
  const n = Number(count) || 0;
  return (
    <span
      aria-label={`${n} tickets`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 18,
        height: 18,
        padding: '0 5px',
        marginLeft: 6,
        borderRadius: 999,
        fontSize: '0.68rem',
        fontWeight: 700,
        lineHeight: 1,
        background: active ? 'rgba(255,255,255,0.3)' : '#e5e7eb',
        color: active ? '#fff' : '#374151',
      }}
    >
      {n}
    </span>
  );
}

function AssigneeTag({ name }) {
  if (!name) return null;
  return (
    <span
      title={`Toegewezen aan ${name}`}
      style={{
        fontSize: '0.65rem',
        fontWeight: 600,
        color: '#065f46',
        background: '#d1fae5',
        padding: '0.1rem 0.35rem',
        borderRadius: 4,
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      @{name}
    </span>
  );
}

/** Directe preview uit de lijstrij terwijl het volledige ticket nog laadt. */
function ticketListRowToDetailStub(row) {
  if (!row) return null;
  return {
    ...row,
    body: row.body_preview || row.body || '',
    body_html: null,
    ai_reply: null,
    final_reply: null,
    attachments: [],
    attachments_checked: false,
    attachments_error: null,
    _detailStub: true,
  };
}

/**
 * Converteert mail-HTML naar plain tekst waarbij alinea's / regeleinden /
 * lijsten bewaard blijven, en HTML-entiteiten (&nbsp;, &amp;, &lt;, ...) netjes
 * gedecodeerd worden. Bedoeld voor het "quote"-blok onder een reply.
 */
function htmlToQuotedText(html) {
  const raw = String(html || '');
  if (!raw.trim()) return '';
  try {
    const doc = new DOMParser().parseFromString(raw, 'text/html');
    doc.querySelectorAll('script,style,head').forEach((n) => n.remove());
    // Blok-elementen markeren zodat textContent nette regeleinden krijgt.
    doc.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
    const blocks = 'p,div,li,tr,h1,h2,h3,h4,h5,h6,blockquote,pre,section,article';
    doc.querySelectorAll(blocks).forEach((el) => {
      el.append('\n');
    });
    doc.querySelectorAll('tr').forEach((el) => el.append('\n'));
    let text = doc.body?.textContent || doc.documentElement?.textContent || '';
    text = text
      .replace(/\u00a0/g, ' ')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
    return text;
  } catch (_) {
    return '';
  }
}

/**
 * Bouwt een "quote" blok onder het antwoord met het originele bericht,
 * zodat de medewerker tijdens het typen context kan blijven lezen (en het
 * ook meegestuurd wordt in de reply zoals bij reguliere mailclients).
 */
function buildQuotedOriginal(detail) {
  if (!detail) return '';
  // Bij voorkeur uit body_html halen zodat alinea's/regeleinden intact blijven.
  // De server-side `body` is een plat-tekstversie waarin whitespace is
  // samengetrokken tot enkele spaties, die zou één lange run-on regel geven.
  let source = htmlToQuotedText(detail.body_html);
  if (!source) source = String(detail.body || '').trim();
  if (!source) return '';
  const date = detail.received_at
    ? new Date(detail.received_at).toLocaleString('nl-NL')
    : '';
  const from = detail.from_name
    ? `${detail.from_name} <${detail.from_email}>`
    : detail.from_email || '';
  const quoted = source
    .split(/\r?\n/)
    .map((l) => (l ? `> ${l}` : '>'))
    .join('\n');
  const header = date
    ? `Op ${date} schreef ${from}:`
    : `${from} schreef:`;
  return `\n\n---\n${header}\n\n${quoted}\n`;
}

// Verwijdert gevaarlijke delen uit een inline-style string, maar laat
// legitieme CSS (kleuren, padding, font-family, etc.) staan. Zo blijft de
// originele opmaak van bijv. factuur- of marketing-mails grotendeels intact.
function sanitizeInlineStyle(raw) {
  const s = String(raw || '');
  if (!s) return '';
  let out = s
    .replace(/expression\s*\(/gi, '')
    .replace(/@import[^;]*;?/gi, '')
    .replace(/behavior\s*:[^;]*;?/gi, '')
    .replace(/-moz-binding\s*:[^;]*;?/gi, '');
  out = out.replace(/url\s*\(\s*(['"]?)\s*javascript:[^)]*\)/gi, '');
  return out.trim();
}

function sanitizeEmailHtml(rawHtml) {
  const html = String(rawHtml || '');
  if (!html.trim()) return '';
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    doc.querySelectorAll('script,style,iframe,object,embed,form,meta,base,link').forEach((n) => n.remove());
    doc.querySelectorAll('*').forEach((el) => {
      Array.from(el.attributes).forEach((attr) => {
        const name = String(attr.name || '').toLowerCase();
        const value = String(attr.value || '');
        if (name.startsWith('on')) {
          el.removeAttribute(attr.name);
          return;
        }
        if ((name === 'href' || name === 'src') && /^\s*javascript:/i.test(value)) {
          el.removeAttribute(attr.name);
          return;
        }
        if (name === 'style') {
          const safe = sanitizeInlineStyle(value);
          if (safe) el.setAttribute('style', safe);
          else el.removeAttribute(attr.name);
        }
      });
    });
    // Externe links openen in een nieuw tabblad met veilige rel-attributen.
    doc.querySelectorAll('a[href]').forEach((a) => {
      const href = String(a.getAttribute('href') || '').trim();
      if (/^(https?:|mailto:|tel:)/i.test(href)) {
        a.setAttribute('target', '_blank');
        a.setAttribute('rel', 'noopener noreferrer');
      }
    });
    // Zorg dat grote inline-plaatjes (bv. 3000×4000px product-foto's) nooit uit
    // het preview-venster breken. We houden bestaande style-regels aan, maar
    // voegen altijd een max-width/height toe.
    doc.querySelectorAll('img').forEach((img) => {
      img.removeAttribute('width');
      img.removeAttribute('height');
      const prev = String(img.getAttribute('style') || '').trim();
      const extra = 'max-width:100%;height:auto;';
      img.setAttribute('style', prev ? `${prev};${extra}` : extra);
    });
    // Tabellen in marketing-/factuurmails staan vaak op een vaste px-breedte
    // (bijv. 600px). Clip die op de container-breedte zodat ze niet horizontaal
    // gaan scrollen in de detailweergave.
    doc.querySelectorAll('table').forEach((t) => {
      const prev = String(t.getAttribute('style') || '').trim();
      t.setAttribute('style', prev ? `${prev};max-width:100%;` : 'max-width:100%;');
    });
    return doc.body?.innerHTML || '';
  } catch (_) {
    return '';
  }
}

/** Zelfde allowlist als server `sanitizeComposeUserHtml` (vet/cursief/onderstreept). */
function sanitizeTicketComposeHtml(html) {
  let s = String(html || '');
  s = s.replace(/<script[\s\S]*?<\/script>/gi, '');
  s = s.replace(/<style[\s\S]*?<\/style>/gi, '');
  s = s.replace(/\son\w+\s*=\s*["'][^"']*["']/gi, '');
  s = s.replace(/javascript:/gi, '');
  s = s.replace(/<(\/?)([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/g, (full, slash, tag) => {
    const t = tag.toLowerCase();
    const allowed = ['b', 'strong', 'i', 'em', 'u', 's', 'strike', 'del', 'br', 'p', 'div'];
    if (!allowed.includes(t)) return '';
    if (t === 'br') return slash ? '' : '<br>';
    return `<${slash}${t}>`;
  });
  const visible = s.replace(/<br\s*\/?>/gi, '').replace(/\s/g, '').trim();
  if (!visible) return '<br>';
  return s;
}

function plainToTicketComposeHtml(text) {
  const t = String(text || '');
  if (!t.trim()) return '<br>';
  return t
    .split(/\r?\n/)
    .map((line) => {
      const esc = line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      return esc === '' ? '<br>' : esc;
    })
    .join('<br>');
}

function ticketComposeHtmlToPlain(html) {
  return String(html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/\u00a0/g, ' ')
    .trim();
}

function ticketComposeHtmlIsEmpty(html) {
  return !ticketComposeHtmlToPlain(html);
}

/** Zet vet/cursief/doorhalen/onderstrepen uit als “volgende toetsaanslag”-stijl (contenteditable). */
function resetRichEditorTypingFormats(editorEl) {
  if (!editorEl || typeof document.queryCommandState !== 'function') return;
  try {
    if (document.activeElement !== editorEl) editorEl.focus();
  } catch {
    /* negeren */
  }
  const cmds = ['bold', 'italic', 'strikeThrough', 'underline'];
  for (const cmd of cmds) {
    for (let i = 0; i < 8; i += 1) {
      try {
        if (!document.queryCommandState(cmd)) break;
        document.execCommand(cmd, false, null);
      } catch {
        break;
      }
    }
  }
}

function insertHtmlAtCaret(container, html) {
  if (!container) return;
  try {
    container.focus();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || !container.contains(sel.anchorNode)) {
      container.insertAdjacentHTML('beforeend', html);
      return;
    }
    const range = sel.getRangeAt(0);
    if (!container.contains(range.commonAncestorContainer)) {
      container.insertAdjacentHTML('beforeend', html);
      return;
    }
    range.deleteContents();
    const tpl = document.createElement('template');
    tpl.innerHTML = html;
    const frag = tpl.content;
    range.insertNode(frag);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
  } catch {
    container.insertAdjacentHTML('beforeend', html);
  }
}

function RichFormatToolbar({ editorRef }) {
  const [active, setActive] = useState({ bold: false, italic: false, strikeThrough: false });

  const selectionInEditor = useCallback(() => {
    const el = editorRef?.current;
    if (!el) return false;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return false;
    const root = sel.getRangeAt(0).commonAncestorContainer;
    const node = root.nodeType === Node.TEXT_NODE ? root.parentElement : root;
    return node instanceof Node && el.contains(node);
  }, [editorRef]);

  const refreshActive = useCallback(() => {
    if (!selectionInEditor()) {
      setActive({ bold: false, italic: false, strikeThrough: false });
      return;
    }
    try {
      setActive({
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        strikeThrough: document.queryCommandState('strikeThrough'),
      });
    } catch {
      setActive({ bold: false, italic: false, strikeThrough: false });
    }
  }, [selectionInEditor]);

  useEffect(() => {
    const onSel = () => {
      refreshActive();
    };
    document.addEventListener('selectionchange', onSel);
    return () => document.removeEventListener('selectionchange', onSel);
  }, [refreshActive]);

  const run = (cmd) => {
    const el = editorRef?.current;
    if (!el) return;
    el.focus();
    try {
      document.execCommand(cmd, false, null);
    } catch {
      /* negeren */
    }
    requestAnimationFrame(refreshActive);
  };

  const btnStyle = (isOn) => ({
    fontSize: '0.78rem',
    fontWeight: 700,
    padding: '0.2rem 0.45rem',
    border: `1px solid ${isOn ? '#00b8a9' : '#d1d5db'}`,
    borderRadius: 6,
    background: isOn ? 'rgba(0, 184, 169, 0.12)' : '#fff',
    color: isOn ? '#00796b' : '#4b5563',
    boxShadow: isOn ? 'inset 0 0 0 1px rgba(0, 184, 169, 0.35)' : 'none',
    cursor: 'pointer',
    minWidth: 28,
    transition: 'background 0.12s ease, border-color 0.12s ease, color 0.12s ease, box-shadow 0.12s ease',
  });

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6, alignItems: 'center' }}>
      <button
        type="button"
        title="Vet"
        aria-pressed={active.bold}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => run('bold')}
        style={btnStyle(active.bold)}
      >
        B
      </button>
      <button
        type="button"
        title="Cursief"
        aria-pressed={active.italic}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => run('italic')}
        style={btnStyle(active.italic)}
      >
        I
      </button>
      <button
        type="button"
        title="Doorhalen"
        aria-pressed={active.strikeThrough}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => run('strikeThrough')}
        style={btnStyle(active.strikeThrough)}
      >
        <span style={{ textDecoration: 'line-through', textDecorationThickness: 'from-font' }}>S</span>
      </button>
    </div>
  );
}

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(`(max-width: ${breakpoint}px)`).matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, [breakpoint]);
  return isMobile;
}

export default function Tickets() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState('open');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailEnriching, setDetailEnriching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [bolReprocessBusy, setBolReprocessBusy] = useState(false);
  const [mailboxes, setMailboxes] = useState([]);
  const [mailboxAliases, setMailboxAliases] = useState({});
  const [filterMailbox, setFilterMailbox] = useState('');
  const [users, setUsers] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [onlyMine, setOnlyMine] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusCounts, setStatusCounts] = useState({ open: 0, inbehandeling: 0, afgehandeld: 0, alle: 0, mine: 0 });
  const [ticketsKlaarMelding, setTicketsKlaarMelding] = useState(null);
  const [ticketsKlaarBannerDismissed, setTicketsKlaarBannerDismissed] = useState(false);
  const [ticketsKlaarBannerMelding, setTicketsKlaarBannerMelding] = useState(null);
  const prevTicketsOpenQueueRef = useRef(null);
  const [statsReady, setStatsReady] = useState(false);
  const ticketsKlaarBannerDismissedRef = useRef(false);
  const [soundEnabled, setSoundEnabled] = useState(() => isTicketSoundEnabled());
  // Standaard antwoorden (canned replies) van de huidige gebruiker, geladen
  // bij mount. Worden als chips getoond onder het antwoordveld.
  const [cannedReplies, setCannedReplies] = useState([]);
  const replyEditorRef = useRef(null);
  const composeEditorRef = useRef(null);
  const loadDetailTokenRef = useRef(0);
  const detailPrefetchRef = useRef(new Map());
  const [showCompose, setShowCompose] = useState(false);
  const [compose, setCompose] = useState({
    mailbox: '',
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    attachments: [],
  });
  const [translatedBody, setTranslatedBody] = useState('');
  const [translatedBodyIsHtml, setTranslatedBodyIsHtml] = useState(false);
  const [targetLanguage, setTargetLanguage] = useState('Nederlands');
  const [showHtmlBody, setShowHtmlBody] = useState(true);
  const [showOriginalPopup, setShowOriginalPopup] = useState(false);
  const [showReplyOptions, setShowReplyOptions] = useState(false);
  // AI-samenvatting: standaard ingeklapt zodat de detailweergave rustiger oogt.
  const [aiSummaryOpen, setAiSummaryOpen] = useState(false);
  // Dropdown-menu voor AI / vertaal-acties; samengevoegd om de antwoord-regel op te schonen.
  const [aiMenuOpen, setAiMenuOpen] = useState(false);
  const aiMenuRef = useRef(null);
  // Preview-modal voor bijlagen: null = dicht, anders { name, contentType, url, loading, text }
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  // Doorsturen: klein inline-paneel onder het antwoordblok.
  const [showForward, setShowForward] = useState(false);
  const [forwardTo, setForwardTo] = useState('');
  const [forwardCc, setForwardCc] = useState('');
  const [forwardComment, setForwardComment] = useState('');
  const forwardPanelRef = useRef(null);
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);
  const settingsRef = useRef({ aiContext: '', defaultSignature: '', mailboxSignatures: {} });

  const buildSignature = useCallback((mailbox) => {
    const s = settingsRef.current || {};
    const mb = String(mailbox || '').trim();
    if (mb && s.mailboxSignatures && s.mailboxSignatures[mb]) return String(s.mailboxSignatures[mb]);
    return String(s.defaultSignature || '');
  }, []);

  const withSignature = useCallback(
    (text, mailbox) => {
      const base = String(text || '').trim();
      const sig = buildSignature(mailbox).trim();
      if (!sig) return base;
      if (base && base.includes(sig)) return text || '';
      return base ? `${base}\n\n${sig}` : `\n\n${sig}`;
    },
    [buildSignature]
  );

  const composeReply = useCallback(
    (aiText, ticket) => {
      const withSig = withSignature(aiText || '', ticket?.mailbox);
      const quote = buildQuotedOriginal(ticket);
      return quote ? `${withSig}${quote}` : withSig;
    },
    [withSignature]
  );

  // Plak een standaard antwoord op de cursorpositie in het antwoordveld (rich).
  const insertCannedReply = useCallback((body) => {
    const text = String(body || '');
    if (!text) return;
    const el = replyEditorRef.current;
    const prevEditorScroll = el ? el.scrollTop : 0;
    const prevWindowScroll =
      typeof window !== 'undefined' ? window.scrollY || window.pageYOffset || 0 : 0;
    const chunk = plainToTicketComposeHtml(text);
    if (!el) return;
    insertHtmlAtCaret(el, chunk);
    el.innerHTML = sanitizeTicketComposeHtml(el.innerHTML);
    resetRichEditorTypingFormats(el);
    queueMicrotask(() => {
      try {
        el.focus({ preventScroll: true });
        el.scrollTop = prevEditorScroll;
        if (typeof window !== 'undefined') {
          window.scrollTo({ top: prevWindowScroll, left: window.scrollX || 0 });
        }
      } catch {
        /* negeren */
      }
    });
  }, []);

  const applyTicketsKlaarCelebration = useCallback((counts, { showModal = false, forceModal = false } = {}) => {
    if (searchQuery.trim()) return;
    if (!shouldShowTicketsKlaarCelebration(counts)) return;

    markTicketsInboxZeroToday();
    clearTicketsKlaarBannerDismissedToday();
    const payload = buildTicketsKlaarMeldingPayload(counts);
    setTicketsKlaarBannerDismissed(false);
    ticketsKlaarBannerDismissedRef.current = false;
    setTicketsKlaarBannerMelding((prev) => prev || payload);

    if (showModal) {
      if (forceModal) {
        setTicketsKlaarMelding(payload);
      } else if (!wasTicketsKlaarModalShownToday()) {
        markTicketsKlaarModalShownToday();
        setTicketsKlaarMelding(payload);
      }
    }
  }, [searchQuery]);

  const loadList = useCallback(async () => {
    const params = new URLSearchParams();
    if (filter !== 'alle') params.set('status', filter);
    if (filterMailbox) params.set('mailbox', filterMailbox);
    if (onlyMine) params.set('assigned_to', 'me');
    if (searchQuery.trim()) params.set('q', searchQuery.trim());
    const q = params.toString() ? `?${params.toString()}` : '';

    const statsParams = new URLSearchParams();
    if (filterMailbox) statsParams.set('mailbox', filterMailbox);
    if (onlyMine) statsParams.set('assigned_to', 'me');
    if (searchQuery.trim()) statsParams.set('q', searchQuery.trim());
    const qs = statsParams.toString() ? `?${statsParams.toString()}` : '';

    // Parallel i.p.v. in serie: scheelt één roundtrip aan wachttijd.
    const [listRes, statsRes] = await Promise.all([
      api.get(`/tickets${q}`),
      api.get(`/tickets/stats${qs}`).catch(() => null),
    ]);

    const list = Array.isArray(listRes.data) ? listRes.data : [];
    setTickets(list);

    let counts = statsRes?.data ? parseTicketsStatusCounts(statsRes.data) : null;
    if (!counts) {
      try {
        const fallback = await api.get('/tickets/stats');
        if (fallback?.data) counts = parseTicketsStatusCounts(fallback.data);
      } catch {
        /* stats niet beschikbaar */
      }
    }
    if (counts) {
      setStatusCounts(counts);
      setStatsReady(true);

      if (
        !searchQuery.trim()
        && shouldShowTicketsKlaarCelebration(counts)
        && !ticketsKlaarBannerDismissedRef.current
      ) {
        applyTicketsKlaarCelebration(counts, {
          showModal: true,
          forceModal: false,
        });
      }
    } else {
      setStatsReady(true);
    }
  }, [filter, filterMailbox, onlyMine, searchQuery, applyTicketsKlaarCelebration]);

  // Debounce: zoeken triggert pas 300ms na laatste toetsaanslag, om te voorkomen dat
  // elke letter een API-call veroorzaakt.
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Escape sluit open overlays (bijlage-preview > popup > compose).
  useEffect(() => {
    if (!attachmentPreview && !showOriginalPopup && !showCompose) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (attachmentPreview) {
        // Inline sluit-logica: de bijbehorende helper wordt pas verderop in het
        // component gedefinieerd, dus we dupliceren het hier om TDZ-fouten te
        // voorkomen. Blob-URL vrijgeven voordat we de state op null zetten.
        setAttachmentPreview((prev) => {
          if (prev?.url) {
            try { URL.revokeObjectURL(prev.url); } catch { /* negeren */ }
          }
          return null;
        });
      } else if (showOriginalPopup) setShowOriginalPopup(false);
      else if (showCompose && !busy) setShowCompose(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [attachmentPreview, showOriginalPopup, showCompose, busy]);

  // Wanneer het doorstuur-paneel opent, rollen we het automatisch in beeld en
  // zetten we de focus op het Aan-veld. Anders staat het paneel onderaan de
  // pagina en lijkt er "niets" te gebeuren bij het klikken op de knop.
  useEffect(() => {
    if (!showForward) return;
    const t = setTimeout(() => {
      const el = forwardPanelRef.current;
      if (el) {
        try {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const firstInput = el.querySelector('input[type="text"]');
          if (firstInput) firstInput.focus({ preventScroll: true });
        } catch { /* negeren */ }
      }
    }, 30);
    return () => clearTimeout(t);
  }, [showForward]);

  // Sluit het AI-dropdownmenu bij klikken buiten het menu of bij Escape.
  useEffect(() => {
    if (!aiMenuOpen) return undefined;
    const onDoc = (e) => {
      if (aiMenuRef.current && !aiMenuRef.current.contains(e.target)) {
        setAiMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setAiMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [aiMenuOpen]);

  // Eénmalige fetches: mailbox-info, user-instellingen, gebruikerslijst en
  // huidige gebruiker. Deze hoeven NIET opnieuw bij elke filterwijziging, dus
  // staan in een aparte effect zonder `loadList` als dependency.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const settle = (p) => p.then((r) => ({ ok: true, data: r.data })).catch(() => ({ ok: false }));
      const [info, settings, users, me, canned] = await Promise.all([
        settle(api.get('/tickets/outlook-info')),
        settle(api.get('/tickets/settings')),
        settle(api.get('/tickets/users')),
        settle(api.get('/auth/me')),
        settle(api.get('/tickets/canned-replies')),
      ]);
      if (cancelled) return;

      if (canned.ok && Array.isArray(canned.data?.replies)) {
        setCannedReplies(
          canned.data.replies
            .map((r) => ({ title: String(r.title || ''), body: String(r.body || '') }))
            .filter((r) => r.title && r.body)
        );
      } else {
        setCannedReplies([]);
      }

      if (info.ok && info.data?.configured && Array.isArray(info.data.mailboxes)) {
        setMailboxes(info.data.mailboxes);
        setMailboxAliases(
          info.data?.mailboxAliases && typeof info.data.mailboxAliases === 'object'
            ? info.data.mailboxAliases
            : {}
        );
      } else {
        setMailboxes([]);
        setMailboxAliases({});
      }

      if (settings.ok) {
        settingsRef.current = {
          aiContext: String(settings.data?.aiContext || ''),
          defaultSignature: String(settings.data?.defaultSignature || ''),
          mailboxSignatures:
            settings.data?.mailboxSignatures && typeof settings.data.mailboxSignatures === 'object'
              ? settings.data.mailboxSignatures
              : {},
        };
      } else {
        settingsRef.current = { aiContext: '', defaultSignature: '', mailboxSignatures: {} };
      }

      setUsers(users.ok && Array.isArray(users.data) ? users.data : []);

      if (me.ok) {
        const uid = me.data?.user?.id ?? me.data?.id ?? null;
        setCurrentUserId(uid ? parseInt(uid, 10) : null);
      } else {
        setCurrentUserId(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Filter-reactieve fetch: tickets + stats. Draait initieel én bij elk
  // filter- / zoek- / mailbox-verandering.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        await loadList();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadList]);

  const showTicketsKlaarBanner =
    statsReady &&
    !searchQuery.trim() &&
    shouldShowTicketsKlaarCelebration(statusCounts) &&
    !ticketsKlaarBannerDismissed;

  useEffect(() => {
    ticketsKlaarBannerDismissedRef.current = ticketsKlaarBannerDismissed;
  }, [ticketsKlaarBannerDismissed]);

  useEffect(() => {
    if (!showTicketsKlaarBanner || ticketsKlaarBannerMelding) return;
    setTicketsKlaarBannerMelding(buildTicketsKlaarMeldingPayload(statusCounts));
  }, [showTicketsKlaarBanner, statusCounts, ticketsKlaarBannerMelding]);

  useEffect(() => {
    if (!statsReady) return;
    const queue = ticketsOpenQueueCount(statusCounts);
    if (searchQuery.trim()) {
      prevTicketsOpenQueueRef.current = queue;
      return;
    }
    const prev = prevTicketsOpenQueueRef.current;
    if (prev !== null && prev > 0 && queue === 0) {
      markTicketsHadOpenQueue();
      applyTicketsKlaarCelebration(statusCounts, { showModal: true, forceModal: true });
    }
    if (queue > 0) {
      markTicketsHadOpenQueue();
    }
    prevTicketsOpenQueueRef.current = queue;
  }, [statsReady, statusCounts, searchQuery, applyTicketsKlaarCelebration]);

  const dismissTicketsKlaarBanner = () => {
    dismissTicketsKlaarBannerForToday();
    ticketsKlaarBannerDismissedRef.current = true;
    setTicketsKlaarBannerDismissed(true);
    setTicketsKlaarBannerMelding(null);
  };

  const [thread, setThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState('');
  // Of het hele conversatie-blok uitgeklapt is (default: ja, zodra er data is).
  const [threadExpanded, setThreadExpanded] = useState(true);
  // Per-bericht openstaan bijhouden — default zijn alle berichten dicht, zodat
  // je eerst het overzicht ziet. Key = msg.id of index.
  const [openMessageIds, setOpenMessageIds] = useState(() => new Set());

  // Voorkomt race-conditions: als je snel achter elkaar van ticket wisselt, kan
  // een late response van het vorige ticket anders de thread overschrijven
  // waardoor je "even verzonden items ziet en dan zijn ze weer weg".
  const threadRequestIdRef = useRef(0);

  const loadThread = useCallback(async (id) => {
    if (!id) return;
    const reqId = ++threadRequestIdRef.current;
    setThreadLoading(true);
    setThreadError('');
    try {
      const res = await api.get(`/tickets/${id}/thread`);
      if (reqId !== threadRequestIdRef.current) return; // achterhaald
      const msgs = Array.isArray(res.data?.messages) ? res.data.messages : [];
      setThread(msgs);
      // Server geeft soms een waarschuwing mee (bv. Graph faalde maar we tonen
      // lokale reply wél). Laat die zachtjes zien zodat je weet waarom er
      // mogelijk minder in de thread staat.
      if (res.data?.graph_error) {
        setThreadError(`Outlook kon de volledige conversatie niet leveren (${res.data.graph_error}). Lokaal bewaarde verzonden mail is wel zichtbaar.`);
      } else if (res.data?.note) {
        setThreadError(String(res.data.note));
      }
    } catch (e) {
      if (reqId !== threadRequestIdRef.current) return;
      setThreadError(e.response?.data?.error || e.message || 'Kon conversatie niet laden');
      setThread([]);
    } finally {
      if (reqId === threadRequestIdRef.current) setThreadLoading(false);
    }
  }, []);

  const applyDetailEnrich = useCallback((id, token, patch) => {
    if (!patch || token !== loadDetailTokenRef.current) return;
    setDetail((prev) => {
      if (!prev || prev.id !== id) return prev;
      return {
        ...prev,
        body_html: patch.body_html != null ? patch.body_html : prev.body_html,
        has_attachments_hint: patch.has_attachments_hint ?? prev.has_attachments_hint,
        attachments: patch.attachments ?? prev.attachments,
        attachments_checked: patch.attachments_checked ?? prev.attachments_checked,
        attachments_error: patch.attachments_error ?? prev.attachments_error,
      };
    });
  }, []);

  const loadDetailEnrich = useCallback(
    async (id, token) => {
      const finish = () => {
        if (token === loadDetailTokenRef.current) setDetailEnriching(false);
      };
      try {
        const res = await api.get(`/tickets/${id}/enrich`);
        applyDetailEnrich(id, token, res.data);
        finish();
        return;
      } catch (e) {
        const status = e.response?.status;
        // Oude server zonder /enrich: fallback naar ?outlook=1 op het detail-endpoint.
        if (status === 404) {
          try {
            const fallback = await api.get(`/tickets/${id}`, { params: { outlook: 1 } });
            if (token !== loadDetailTokenRef.current) return;
            const d = fallback.data || {};
            applyDetailEnrich(id, token, {
              body_html: d.body_html,
              has_attachments_hint: d.has_attachments_hint,
              attachments: d.attachments,
              attachments_checked: d.attachments_checked,
              attachments_error: d.attachments_error,
            });
            finish();
            return;
          } catch (e2) {
            e = e2;
          }
        }
        const msg =
          e.response?.data?.error ||
          e.message ||
          'Bijlagen konden niet uit Outlook worden geladen';
        if (token === loadDetailTokenRef.current) {
          setDetail((prev) =>
            prev && prev.id === id
              ? {
                  ...prev,
                  attachments_checked: true,
                  attachments_error: msg,
                }
              : prev
          );
        }
      } finally {
        finish();
      }
    },
    [applyDetailEnrich]
  );

  const prefetchTicketDetail = useCallback((id) => {
    if (!id || detailPrefetchRef.current.has(id)) return;
    const p = api.get(`/tickets/${id}`).finally(() => {
      detailPrefetchRef.current.delete(id);
    });
    detailPrefetchRef.current.set(id, p);
  }, []);

  const loadDetail = useCallback(
    async (id) => {
      if (!id) {
        setDetail(null);
        setDetailLoading(false);
        setDetailEnriching(false);
        return;
      }
      threadRequestIdRef.current += 1;
      setThread(null);
      setThreadError('');
      setThreadLoading(false);
      setOpenMessageIds(new Set());
      setThreadExpanded(true);

      const token = ++loadDetailTokenRef.current;
      setDetailLoading(true);
      setDetailEnriching(true);
      setTranslatedBody('');
      setTranslatedBodyIsHtml(false);
      setCc('');
      setBcc('');
      setAttachments([]);

      try {
        let res;
        const cached = detailPrefetchRef.current.get(id);
        if (cached) {
          res = await cached;
          detailPrefetchRef.current.delete(id);
        } else {
          res = await api.get(`/tickets/${id}`);
        }
        if (token !== loadDetailTokenRef.current) return;

        setDetail(res.data);
        setDetailLoading(false);
        const plainReply = composeReply(res.data.ai_reply || '', res.data);
        setTimeout(() => {
          if (token !== loadDetailTokenRef.current) return;
          const el = replyEditorRef.current;
          if (el) {
            el.innerHTML = plainToTicketComposeHtml(plainReply);
            resetRichEditorTypingFormats(el);
          }
        }, 0);

        loadDetailEnrich(id, token);

        const needsThread =
          !!res.data?.conversation_id || !!res.data?.replied_at || !!res.data?.final_reply;
        if (needsThread) {
          loadThread(id).catch(() => {
            /* stille fallback, foutmelding staat in threadError */
          });
        }
      } catch (e) {
        if (token !== loadDetailTokenRef.current) return;
        setDetailLoading(false);
        setDetailEnriching(false);
        alert(e.response?.data?.error || e.message || 'Ticket laden mislukt');
      }
    },
    [loadThread, loadDetailEnrich]
  );

  const selectTicket = useCallback(
    (id) => {
      if (!id || id === selectedId) return;
      const stub = tickets.find((t) => t.id === id);
      if (stub) setDetail(ticketListRowToDetailStub(stub));
      else setDetail(null);
      setSelectedId(id);
    },
    [selectedId, tickets]
  );

  useEffect(() => {
    if (selectedId) {
      loadDetail(selectedId);
    } else {
      threadRequestIdRef.current += 1;
      setDetail(null);
      setDetailLoading(false);
      setDetailEnriching(false);
      setThread(null);
      setThreadError('');
      setThreadLoading(false);
    }
  }, [selectedId, loadDetail]);

  // --- Presence: wie heeft welk ticket nu open? -----------------------------
  // We sturen een heartbeat (~15s) voor het ticket dat deze client op dit moment
  // bekijkt, en pollen daarnaast ~20s een globale snapshot zodat collega's in de
  // lijst zichtbaar zijn. Dit is bewust "best effort" / in-memory op de server;
  // een gemiste ping geeft hoogstens een korte false-positive van maximaal ~35s.
  const [presence, setPresence] = useState({}); // { [ticketId]: [{ userId, userName, lastSeenAt }] }

  const loadPresence = useCallback(async () => {
    try {
      const r = await api.get('/tickets/presence');
      setPresence(r.data?.presence || {});
    } catch {
      /* presence-errors zijn niet kritisch */
    }
  }, []);

  useEffect(() => {
    loadPresence();
    const t = setInterval(loadPresence, 20000);
    return () => clearInterval(t);
  }, [loadPresence]);

  // Heartbeat voor het ticket dat op dit moment open is.
  useEffect(() => {
    if (!selectedId) return undefined;
    let cancelled = false;
    const ping = async () => {
      try {
        await api.post(`/tickets/${selectedId}/presence`);
        if (!cancelled) loadPresence();
      } catch {
        /* stille fallback */
      }
    };
    ping();
    const t = setInterval(ping, 15000);
    return () => {
      cancelled = true;
      clearInterval(t);
      // Expliciet loslaten zodat collega's niet nog 30s een stale indicator zien.
      api.delete(`/tickets/${selectedId}/presence`).catch(() => {});
    };
  }, [selectedId, loadPresence]);

  // Bij tab-sluiten vertrouwen we op server-side TTL (~35s) om stale entries op
  // te ruimen; een synchrone leave-call via sendBeacon is onbetrouwbaar met
  // authenticatie en hoeft hier niet.

  // Auto-select eerste ticket uit de lijst zodra er tickets zijn (en selectie nog leeg
  // is of niet meer in de gefilterde lijst voorkomt). Op mobiel slaan we dit over:
  // daar toont het direct openen van een ticket de lijst niet meer, wat verwarrend is.
  useEffect(() => {
    if (isMobile) return;
    if (!tickets.length) {
      if (selectedId !== null) setSelectedId(null);
      return;
    }
    if (!selectedId || !tickets.some((t) => t.id === selectedId)) {
      const first = tickets[0];
      setDetail(ticketListRowToDetailStub(first));
      setSelectedId(first.id);
    }
  }, [tickets, selectedId, isMobile]);

  useEffect(() => {
    setSelectedId(null);
  }, [filterMailbox]);

  const handleSync = async () => {
    if (!window.confirm('Nu synchroniseren met Outlook (ongezen → tickets)?')) return;
    setSyncing(true);
    try {
      const res = await api.post('/tickets/sync');
      await loadList();
      window.dispatchEvent(new Event('tickets-stats-refresh'));
      if (res.data && res.data.success === false && res.data.error) {
        alert(`Sync mislukt: ${res.data.error}`);
        return;
      }
      let msg = `Sync: ${res.data.created ?? 0} nieuw, ${res.data.skippedDup ?? 0} al bekend`;
      if (res.data.skipped && res.data.reason) {
        const why =
          res.data.reason === 'outlook_not_configured'
            ? 'Outlook/Azure staat niet op de server (controleer Railway-variabelen).'
            : res.data.reason === 'no_mailboxes'
              ? 'Geen postvakken in OUTLOOK_MAILBOXES / OUTLOOK_MAILBOX.'
              : res.data.reason;
        msg = `Let op: ${why}\n\n${msg}`;
      }
      if (Array.isArray(res.data.mailboxes) && res.data.mailboxes.length) {
        msg += `\n\nPer postvak:\n${res.data.mailboxes
          .map((m) =>
            m.error ? `${m.mailbox}: fout — ${m.error}` : `${m.mailbox}: ${m.checked ?? 0} ongelezen gezien, ${m.created ?? 0} nieuw`
          )
          .join('\n')}`;
      }
      if ((res.data.errors || []).length) msg += `\n\n${res.data.errors.length} waarschuwing(en) in log.`;
      alert(msg);
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleReprocessBolFromTickets = async () => {
    if (
      !window.confirm(
        'Open tickets waarvan het onderwerp "Retourmelding:" of "Return notification:" bevat doorlopen en waar mogelijk automatisch een retour in het magazijn aanmaken?\n\n' +
          '• Alleen die onderwerpregel telt (niet alle Bol-mails), zodat het snel blijft.\n' +
          '• Kleine batches op de server (meerdere requests), zodat Railway het proces niet afbreekt.\n' +
          '• Al gekoppelde retouren worden overgeslagen.\n' +
          '• Bij succes wordt het ticket op afgehandeld gezet (zelfde als bij live sync).'
      )
    ) {
      return;
    }
    setBolReprocessBusy(true);
    try {
      const batch = 8;
      let offset = 0;
      let totalScanned = 0;
      let totalCreated = 0;
      const aggReasons = {};
      const wooNotFoundRefs = [];
      let batchIndex = 0;
      for (;;) {
        batchIndex += 1;
        const res = await api.post(
          '/tickets/reprocess-bol-retours',
          { limit: batch, offset, onlyOpen: true, bolLikelyOnly: true },
          { timeout: 120000 }
        );
        const d = res.data || {};
        const n = d.scanned ?? 0;
        totalScanned += n;
        totalCreated += d.retouren_created ?? 0;
        Object.entries(d.reason_counts || {}).forEach(([k, v]) => {
          aggReasons[k] = (aggReasons[k] || 0) + v;
        });
        (d.details || []).forEach((row) => {
          if (row.reason === 'woo_order_not_found' && row.bol_ref) {
            wooNotFoundRefs.push(String(row.bol_ref));
          }
        });
        if (n === 0 || !d.has_more) break;
        offset = typeof d.next_offset === 'number' ? d.next_offset : offset + n;
        if (batchIndex >= 200) break;
      }
      const parts = Object.keys(aggReasons).map((k) => `${k}: ${aggReasons[k]}`);
      let msg = `Totaal gescand: ${totalScanned}\nRetouren aangemaakt: ${totalCreated}\n(Batches: ${batchIndex})`;
      if (parts.length) msg += `\n\n${parts.join('\n')}`;
      if (wooNotFoundRefs.length) {
        const uniq = [...new Set(wooNotFoundRefs)];
        msg += `\n\nGeen Woo-order voor Bol-ref:\n${uniq.slice(0, 12).join('\n')}`;
        if (uniq.length > 12) msg += `\n… en ${uniq.length - 12} meer`;
      }
      msg += '\n\n(Zie Railway-log voor batch-details.)';
      window.alert(msg);
      await loadList();
      try {
        window.dispatchEvent(new Event('tickets-stats-refresh'));
        window.dispatchEvent(new Event('magazijn-orders-refresh'));
      } catch (_) {
        /* noop */
      }
    } catch (e) {
      window.alert(e.response?.data?.error || e.message);
    } finally {
      setBolReprocessBusy(false);
    }
  };

  const handleAnalyze = async () => {
    if (!selectedId) return;
    setBusy(true);
    try {
      await api.post(`/tickets/${selectedId}/analyze`, { context: settingsRef.current.aiContext });
      await loadDetail(selectedId);
      await loadList();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleSuggest = async () => {
    if (!selectedId) return;
    setBusy(true);
    try {
      const res = await api.post(`/tickets/${selectedId}/reply-suggestion`, {
        context: settingsRef.current.aiContext,
      });
      const plain = composeReply(res.data.ai_reply || '', detail);
      const el = replyEditorRef.current;
      if (el) {
        el.innerHTML = plainToTicketComposeHtml(plain);
        resetRichEditorTypingFormats(el);
      }
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleTranslate = async () => {
    if (!selectedId) return;
    setBusy(true);
    try {
      // Geen `text` meesturen → server pakt de HTML-body als die er is en
      // zet `is_html: true`, zodat de opmaak (paragrafen, vetgedrukt, links)
      // behouden blijft in de vertaling.
      const res = await api.post(`/tickets/${selectedId}/translate`, {
        targetLanguage,
        context: settingsRef.current.aiContext,
      });
      setTranslatedBody(res.data?.translated_text || '');
      setTranslatedBodyIsHtml(Boolean(res.data?.is_html));
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  // Haalt één bijlage op als Blob via de beveiligde API (JWT-header wordt
  // automatisch meegestuurd). Return: { blob, url } of gooit fout.
  const fetchAttachmentBlob = useCallback(async (ticketId, attachment) => {
    const res = await api.get(
      `/tickets/${ticketId}/attachments/${encodeURIComponent(attachment.id)}/download`,
      { responseType: 'blob' }
    );
    const blob = new Blob([res.data], {
      type: attachment.contentType || 'application/octet-stream',
    });
    const url = URL.createObjectURL(blob);
    return { blob, url };
  }, []);

  // Direct downloaden: triggert klik op een verborgen <a download>.
  const handleDownloadAttachment = async (ticketId, attachment) => {
    if (!ticketId || !attachment?.id) return;
    try {
      setBusy(true);
      const { url } = await fetchAttachmentBlob(ticketId, attachment);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.name || 'bijlage';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      alert(e.response?.data?.error || e.message || 'Downloaden mislukt');
    } finally {
      setBusy(false);
    }
  };

  // Bepaalt of we een bijlage inline kunnen previewen. PDF, afbeeldingen en
  // platte tekst zijn veilig in een iframe / <img> / <pre>. Andere types (ZIP,
  // Word, Excel, …) vallen terug op download.
  const attachmentKind = (contentType, name) => {
    const ct = String(contentType || '').toLowerCase();
    const ext = String(name || '').split('.').pop()?.toLowerCase() || '';
    if (ct === 'application/pdf' || ext === 'pdf') return 'pdf';
    if (ct.startsWith('image/')) return 'image';
    if (ct.startsWith('text/') || ['txt', 'csv', 'log', 'md'].includes(ext)) return 'text';
    return 'other';
  };

  // Opent de preview-modal voor een bijlage. Voor previewbare types laden we
  // de Blob en maken een object-URL; voor andere types slaan we dat over en
  // tonen we in de modal een download-knop.
  const handleOpenAttachment = async (ticketId, attachment) => {
    if (!ticketId || !attachment?.id) return;
    const kind = attachmentKind(attachment.contentType, attachment.name);
    if (kind === 'other') {
      handleDownloadAttachment(ticketId, attachment);
      return;
    }
    setAttachmentPreview({
      ticketId,
      attachment,
      kind,
      url: null,
      text: '',
      loading: true,
      error: '',
    });
    try {
      const { blob, url } = await fetchAttachmentBlob(ticketId, attachment);
      if (kind === 'text') {
        const txt = await blob.text();
        setAttachmentPreview((prev) =>
          prev && prev.attachment?.id === attachment.id
            ? { ...prev, url, text: txt, loading: false }
            : prev
        );
      } else {
        setAttachmentPreview((prev) =>
          prev && prev.attachment?.id === attachment.id ? { ...prev, url, loading: false } : prev
        );
      }
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Preview mislukt';
      setAttachmentPreview((prev) =>
        prev && prev.attachment?.id === attachment.id ? { ...prev, loading: false, error: msg } : prev
      );
    }
  };

  const closeAttachmentPreview = useCallback(() => {
    setAttachmentPreview((prev) => {
      if (prev?.url) {
        try { URL.revokeObjectURL(prev.url); } catch { /* negeren */ }
      }
      return null;
    });
  }, []);

  const handleTranslateReply = async () => {
    if (!selectedId) return;
    const el = replyEditorRef.current;
    const norm = ticketComposeHtmlToPlain(el?.innerHTML || '').replace(/\r\n/g, '\n');
    if (!norm.trim()) {
      alert('Er staat nog geen antwoordtekst om te vertalen.');
      return;
    }
    const mb = detail?.mailbox || '';
    const sig = buildSignature(mb).trim();
    let textToTranslate = norm;
    let preservedSuffix = '';
    if (sig) {
      const marker = `\n\n${sig}`;
      const idx = norm.indexOf(marker);
      if (idx !== -1) {
        textToTranslate = norm.slice(0, idx);
        preservedSuffix = norm.slice(idx);
      }
    }
    if (!textToTranslate.trim()) {
      alert('Er is niets boven de handtekening om te vertalen.');
      return;
    }
    setBusy(true);
    try {
      const res = await api.post(`/tickets/${selectedId}/translate`, {
        targetLanguage,
        context: settingsRef.current.aiContext,
        text: textToTranslate.trimEnd(),
      });
      const translated = res.data?.translated_text || '';
      const combined = `${translated}${preservedSuffix}`;
      if (el) {
        el.innerHTML = plainToTicketComposeHtml(combined);
        resetRichEditorTypingFormats(el);
      }
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const readFilesAsAttachments = (files) => {
    const list = Array.from(files || []);
    const out = [];
    let chain = Promise.resolve();
    list.forEach((file) => {
      chain = chain.then(
        () =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const r = String(reader.result || '');
              const base64 = r.includes(',') ? r.split(',')[1] : r;
              out.push({
                name: file.name,
                contentType: file.type || 'application/octet-stream',
                contentBase64: base64,
              });
              resolve();
            };
            reader.onerror = () => reject(new Error('Bestand lezen mislukt'));
            reader.readAsDataURL(file);
          })
      );
    });
    return chain.then(() => out);
  };

  const onPickFiles = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    try {
      const next = await readFilesAsAttachments(files);
      setAttachments((a) => [...a, ...next]);
    } catch (err) {
      alert(err.message);
    }
    e.target.value = '';
  };

  const handleSend = async () => {
    const rawHtml = replyEditorRef.current?.innerHTML || '';
    const replyHtmlPayload = sanitizeTicketComposeHtml(rawHtml);
    if (!selectedId || ticketComposeHtmlIsEmpty(replyHtmlPayload)) {
      alert('Vul een antwoordtekst in.');
      return;
    }
    if (!window.confirm('Mail versturen via Outlook?')) return;
    setBusy(true);
    try {
      const ccList = cc
        .split(/[,;\s]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const bccList = bcc
        .split(/[,;\s]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      await api.post(`/tickets/${selectedId}/send`, {
        replyHtml: replyHtmlPayload,
        cc: ccList,
        bcc: bccList,
        attachments,
      });
      await loadList();
      await loadDetail(selectedId);
      window.dispatchEvent(new Event('tickets-stats-refresh'));
      alert('Verzonden.');
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  // Mail doorsturen: we laten Graph het zware werk doen (originele body +
  // bijlagen worden meegestuurd). Na succes sluiten we het paneel en leegmaken
  // de velden, maar we raken de ticketstatus niet aan.
  const handleForward = async () => {
    if (!selectedId) return;
    const toList = forwardTo
      .split(/[,;\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (toList.length === 0) {
      alert('Vul minimaal één ontvanger in.');
      return;
    }
    const ccList = forwardCc
      .split(/[,;\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!window.confirm(`Deze e-mail doorsturen naar ${toList.join(', ')}?`)) return;
    setBusy(true);
    try {
      await api.post(`/tickets/${selectedId}/forward`, {
        to: toList,
        cc: ccList,
        comment: forwardComment.trim(),
      });
      setShowForward(false);
      setForwardTo('');
      setForwardCc('');
      setForwardComment('');
      alert('Doorgestuurd.');
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const openCompose = () => {
    const mb = detail?.mailbox || mailboxes[0] || '';
    const sig = buildSignature(mb);
    setCompose({
      mailbox: mb,
      to: '',
      cc: '',
      bcc: '',
      subject: '',
      attachments: [],
      appliedSignature: sig,
    });
    setShowCompose(true);
    setTimeout(() => {
      const cel = composeEditorRef.current;
      if (cel) {
        cel.innerHTML = '<br>';
        resetRichEditorTypingFormats(cel);
      }
    }, 0);
  };

  const handleComposeMailboxChange = (newMailbox) => {
    setCompose((c) => ({
      ...c,
      mailbox: newMailbox,
      appliedSignature: buildSignature(newMailbox) || '',
    }));
  };

  const handleTranslateCompose = async () => {
    const el = composeEditorRef.current;
    const raw = el?.innerHTML || '';
    if (ticketComposeHtmlIsEmpty(raw)) {
      alert('Typ eerst tekst om te vertalen.');
      return;
    }
    setBusy(true);
    try {
      const safe = sanitizeTicketComposeHtml(raw);
      const res = await api.post('/tickets/translate-text', {
        text: safe,
        targetLanguage,
        context: settingsRef.current.aiContext,
        isHtml: true,
      });
      const t = res.data?.translated_text || '';
      if (el) {
        el.innerHTML = sanitizeTicketComposeHtml(t);
        resetRichEditorTypingFormats(el);
      }
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const onComposePickFiles = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    try {
      const next = await readFilesAsAttachments(files);
      setCompose((c) => ({ ...c, attachments: [...(c.attachments || []), ...next] }));
    } catch (err) {
      alert(err.message);
    }
    e.target.value = '';
  };

  const handleSendCompose = async () => {
    const to = compose.to.trim();
    const subject = compose.subject.trim();
    const rawHtml = composeEditorRef.current?.innerHTML || '';
    const bodyHtmlPayload = sanitizeTicketComposeHtml(rawHtml);
    if (!to) return alert('Vul minstens 1 ontvanger (Aan) in.');
    if (!subject) return alert('Onderwerp is verplicht.');
    if (ticketComposeHtmlIsEmpty(bodyHtmlPayload)) return alert('Berichttekst is verplicht.');
    const mailbox = compose.mailbox || mailboxes[0];
    if (!mailbox) return alert('Geen postvak geconfigureerd.');
    if (!window.confirm(`Verstuur deze e-mail vanuit ${mailboxAliases[mailbox] || mailbox}?`)) return;

    const splitList = (s) =>
      String(s || '')
        .split(/[,;\s]+/)
        .map((x) => x.trim())
        .filter(Boolean);

    setBusy(true);
    try {
      await api.post('/tickets/compose', {
        mailbox,
        to: splitList(compose.to),
        cc: splitList(compose.cc),
        bcc: splitList(compose.bcc),
        subject,
        bodyHtml: bodyHtmlPayload,
        attachments: compose.attachments,
      });
      setShowCompose(false);
      alert('Verzonden.');
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleAssign = async (assignedTo) => {
    if (!selectedId) return;
    setBusy(true);
    try {
      await api.patch(`/tickets/${selectedId}`, {
        assigned_to: assignedTo === '' || assignedTo == null ? null : parseInt(assignedTo, 10),
      });
      await loadDetail(selectedId);
      await loadList();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (status) => {
    if (!selectedId) return;
    setBusy(true);
    try {
      await api.patch(`/tickets/${selectedId}`, { status });
      await loadDetail(selectedId);
      await loadList();
      window.dispatchEvent(new Event('tickets-stats-refresh'));
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  // Snelle actie direct in de lijst: ticket op "afgehandeld" zetten zonder
  // eerst het detail te hoeven openen. Houdt het huidige selected-id intact.
  const [quickBusyIds, setQuickBusyIds] = useState(() => new Set());
  const quickSetStatus = async (id, status) => {
    if (!id) return;
    setQuickBusyIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    try {
      await api.patch(`/tickets/${id}`, { status });
      // Optimistisch de lokale lijst bijwerken, dan in de achtergrond herladen.
      setTickets((prev) => prev.map((t) => (t.id === id ? { ...t, status } : t)));
      if (selectedId === id) {
        setDetail((d) => (d && d.id === id ? { ...d, status } : d));
      }
      await loadList();
      window.dispatchEvent(new Event('tickets-stats-refresh'));
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setQuickBusyIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const ticketsKlaarModal = (
    <Modal
      show={!!ticketsKlaarMelding}
      onClose={() => setTicketsKlaarMelding(null)}
      title=""
      contentClassName="tickets-klaar-modal"
    >
      <div className="tickets-klaar-modal-inner">
        <div className="tickets-klaar-modal-emoji" aria-hidden="true">
          {ticketsKlaarMelding?.emoji || '☕'}
        </div>
        <h3 className="tickets-klaar-modal-title">{ticketsKlaarMelding?.title}</h3>
        <p className="tickets-klaar-modal-body">{ticketsKlaarMelding?.body}</p>
        {ticketsKlaarMelding?.subtekst ? (
          <p className="tickets-klaar-modal-sub">{ticketsKlaarMelding.subtekst}</p>
        ) : null}
        <button
          type="button"
          className="btn btn-primary tickets-klaar-modal-btn"
          onClick={() => setTicketsKlaarMelding(null)}
        >
          Op naar de koffie ☕
        </button>
      </div>
    </Modal>
  );

  const ticketsKlaarBanner = showTicketsKlaarBanner && ticketsKlaarBannerMelding ? (
    <div className="tickets-klaar-banner tickets-klaar-banner-top" role="status">
      <span className="tickets-klaar-banner-emoji" aria-hidden="true">
        {ticketsKlaarBannerMelding.emoji || '☕'}
      </span>
      <div className="tickets-klaar-banner-text">
        <strong className="tickets-klaar-banner-title">{ticketsKlaarBannerMelding.title}</strong>
        <p className="tickets-klaar-banner-body">{ticketsKlaarBannerMelding.body}</p>
        {ticketsKlaarBannerMelding.subtekst ? (
          <p className="tickets-klaar-banner-sub">{ticketsKlaarBannerMelding.subtekst}</p>
        ) : null}
      </div>
      <button
        type="button"
        className="tickets-klaar-banner-close"
        onClick={dismissTicketsKlaarBanner}
        title="Melding sluiten"
        aria-label="Melding sluiten"
      >
        ×
      </button>
    </div>
  ) : null;

  if (loading && !statsReady) {
    return (
      <>
        <div className="loading">Laden...</div>
        {ticketsKlaarModal}
      </>
    );
  }

  // Op mobiel tonen we afwisselend de lijst of het detail i.p.v. allebei
  // naast elkaar — zo krijg je op een klein scherm geen 2 smalle kolommen.
  const showListOnMobile = isMobile && !detail;
  const showDetailOnMobile = isMobile && !!detail;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        height: isMobile ? 'auto' : 'calc(100vh - 120px)',
        minHeight: isMobile ? 0 : 480,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <h2 style={{ margin: 0, color: '#00B8A9', flex: 1, fontSize: isMobile ? '1.2rem' : '1.5rem' }}>
          Tickets
        </h2>
        <button
          type="button"
          onClick={() => {
            const next = !soundEnabled;
            setSoundEnabled(next);
            setTicketSoundEnabled(next);
            // Speel een testgeluid bij inschakelen zodat de gebruiker hoort hoe
            // het klinkt én de AudioContext meteen "unlocked" raakt door de klik.
            if (next) playNewTicketSound({ force: true });
          }}
          title={
            soundEnabled
              ? 'Geluid bij nieuwe ticket staat AAN (klik om uit te zetten)'
              : 'Geluid bij nieuwe ticket staat UIT (klik om aan te zetten)'
          }
          aria-label={soundEnabled ? 'Geluid uitzetten' : 'Geluid aanzetten'}
          aria-pressed={soundEnabled}
          style={{
            background: 'transparent',
            border: '1px solid #d1d5db',
            borderRadius: 999,
            width: 38,
            height: 38,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: soundEnabled ? '#00B8A9' : '#9ca3af',
            fontSize: '1.15rem',
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          {soundEnabled ? (
            <FiBell aria-hidden="true" size={18} />
          ) : (
            <FiBellOff aria-hidden="true" size={18} />
          )}
        </button>
        <button
          type="button"
          onClick={() => navigate('/tickets/instellingen')}
          title="Ticket-instellingen"
          aria-label="Ticket-instellingen"
          style={{
            background: 'transparent',
            border: '1px solid #d1d5db',
            borderRadius: 999,
            width: 38,
            height: 38,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#374151',
            fontSize: '1.15rem',
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
        </button>
      </div>
      {ticketsKlaarBanner}
      <div
        style={{
          display: 'flex',
          flex: 1,
          gap: isMobile ? '0.5rem' : '1rem',
          minHeight: 0,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <div
          style={{
            width: isMobile ? '100%' : 300,
            flexShrink: 0,
            display: showDetailOnMobile ? 'none' : 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
            borderRight: isMobile ? 'none' : '1px solid #e5e7eb',
            paddingRight: isMobile ? 0 : '0.75rem',
            minHeight: 0,
          }}
        >
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
            <button
              className="btn btn-primary"
              type="button"
              onClick={handleSync}
              disabled={syncing || bolReprocessBusy}
              style={{ flex: 1, minWidth: '8rem' }}
            >
              {syncing ? 'Sync…' : 'Sync Outlook'}
            </button>
            <button
              className="btn btn-secondary"
              type="button"
              onClick={openCompose}
              disabled={mailboxes.length === 0 || bolReprocessBusy}
              title={mailboxes.length === 0 ? 'Geen postvak geconfigureerd' : 'Nieuwe e-mail opstellen'}
              style={{ flex: 1, minWidth: '8rem' }}
            >
              + Nieuwe e-mail
            </button>
            <button
              className="btn btn-secondary"
              type="button"
              onClick={handleReprocessBolFromTickets}
              disabled={syncing || bolReprocessBusy}
              title="Doorloopt opgeslagen tickets (Bol / retourmelding) en maakt ontbrekende magazijn-retouren aan"
              style={{ flex: '1 1 100%', fontSize: '0.85rem' }}
            >
              {bolReprocessBusy ? 'Bol-retouren…' : 'Bol-retouren uit tickets'}
            </button>
          </div>
          <div style={{ position: 'relative' }}>
            <input
              type="search"
              placeholder="Zoeken in tickets…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '0.4rem 0.6rem',
                paddingRight: searchInput ? '1.75rem' : '0.6rem',
                border: '1px solid #ccc',
                borderRadius: 6,
                fontSize: '0.85rem',
              }}
            />
            {searchInput ? (
              <button
                type="button"
                onClick={() => setSearchInput('')}
                aria-label="Zoekopdracht wissen"
                style={{
                  position: 'absolute',
                  right: 6,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 0,
                  color: '#6b7280',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  lineHeight: 1,
                  padding: 2,
                }}
              >
                ×
              </button>
            ) : null}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
            {['open', 'inbehandeling', 'afgehandeld', 'alle'].map((f) => {
              const active = filter === f;
              const label = f === 'alle' ? 'Alle' : f === 'inbehandeling' ? 'In behandeling' : f;
              return (
                <button
                  key={f}
                  type="button"
                  className={active ? 'btn btn-primary' : 'btn btn-secondary'}
                  style={{
                    fontSize: '0.8rem',
                    padding: '0.25rem 0.5rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                  onClick={() => {
                    setFilter(f);
                    setSelectedId(null);
                  }}
                >
                  {label}
                  <CountBadge count={statusCounts[f] || 0} active={active} />
                </button>
              );
            })}
          </div>
          {currentUserId ? (
            <button
              type="button"
              className={onlyMine ? 'btn btn-primary' : 'btn btn-secondary'}
              onClick={() => {
                setOnlyMine((v) => !v);
                setSelectedId(null);
              }}
              title="Toon alleen tickets die aan jou zijn toegewezen"
              style={{
                fontSize: '0.8rem',
                padding: '0.3rem 0.55rem',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: 'fit-content',
              }}
            >
              {onlyMine ? '✓ Mijn tickets' : 'Mijn tickets'}
              <CountBadge count={statusCounts.mine || 0} active={onlyMine} />
            </button>
          ) : null}
          {mailboxes.length > 1 ? (
            <label style={{ fontSize: '0.8rem', color: '#444', display: 'flex', flexDirection: 'column', gap: 4 }}>
              Postvak
              <select
                value={filterMailbox}
                onChange={(e) => setFilterMailbox(e.target.value)}
                style={{ padding: '0.35rem 0.5rem', borderRadius: 6, border: '1px solid #ccc', maxWidth: '100%' }}
              >
                <option value="">Alle postvakken</option>
                {mailboxes.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <div
            style={{
              overflowY: isMobile ? 'visible' : 'auto',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
            }}
          >
            {tickets.length === 0 ? (
              showTicketsKlaarBanner ? null : (
                <p style={{ color: '#666', fontSize: '0.9rem' }}>Geen tickets.</p>
              )
            ) : (
              tickets.map((t) => {
                const quickBusy = quickBusyIds.has(t.id);
                const isAfgehandeld = t.status === 'afgehandeld';
                const viewers = Array.isArray(presence[t.id]) ? presence[t.id] : [];
                const otherViewers = viewers.filter(
                  (v) => v && v.userId !== currentUserId
                );
                return (
                  <div
                    key={t.id}
                    role="button"
                    tabIndex={0}
                    onMouseEnter={() => prefetchTicketDetail(t.id)}
                    onFocus={() => prefetchTicketDetail(t.id)}
                    onClick={() => selectTicket(t.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        selectTicket(t.id);
                      }
                    }}
                    style={{
                      textAlign: 'left',
                      padding: '0.65rem',
                      borderRadius: 8,
                      border: selectedId === t.id ? '2px solid #00B8A9' : '1px solid #e5e7eb',
                      background: '#fff',
                      cursor: 'pointer',
                      opacity: isAfgehandeld ? 0.75 : 1,
                      position: 'relative',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.5rem',
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: t.status === 'open' ? 700 : 500,
                            fontSize: '0.9rem',
                            textDecoration: isAfgehandeld ? 'line-through' : 'none',
                            color: isAfgehandeld ? '#6b7280' : 'inherit',
                          }}
                        >
                          {t.subject}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#555', marginTop: 4 }}>
                          {t.from_name || t.from_email}
                        </div>
                      </div>
                      {!isAfgehandeld ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            quickSetStatus(t.id, 'afgehandeld');
                          }}
                          disabled={quickBusy}
                          title="Markeer als afgehandeld"
                          aria-label="Markeer als afgehandeld"
                          style={{
                            flexShrink: 0,
                            width: 28,
                            height: 28,
                            borderRadius: 999,
                            border: '1px solid #d1d5db',
                            background: quickBusy ? '#f3f4f6' : '#fff',
                            color: '#059669',
                            cursor: quickBusy ? 'wait' : 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.95rem',
                            lineHeight: 1,
                            padding: 0,
                          }}
                        >
                          {quickBusy ? '…' : '✓'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            quickSetStatus(t.id, 'open');
                          }}
                          disabled={quickBusy}
                          title="Heropenen"
                          aria-label="Heropenen"
                          style={{
                            flexShrink: 0,
                            width: 28,
                            height: 28,
                            borderRadius: 999,
                            border: '1px solid #d1d5db',
                            background: quickBusy ? '#f3f4f6' : '#ecfdf5',
                            color: '#059669',
                            cursor: quickBusy ? 'wait' : 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.9rem',
                            lineHeight: 1,
                            padding: 0,
                          }}
                        >
                          {quickBusy ? '…' : '↺'}
                        </button>
                      )}
                    </div>
                    <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                      {t.mailbox ? <MailboxTag address={t.mailbox} aliases={mailboxAliases} /> : null}
                      <Badge category={t.category || 'overig'} />
                      <PriorityDot priority={t.priority} />
                      {t.assigned_to_name ? <AssigneeTag name={t.assigned_to_name} /> : null}
                      {otherViewers.length > 0 ? (
                        <span
                          title={`${otherViewers.map((v) => v.userName).join(', ')} heeft dit ticket nu open`}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            background: '#fef3c7',
                            border: '1px solid #fcd34d',
                            color: '#92400e',
                            borderRadius: 999,
                            padding: '1px 8px',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                          }}
                        >
                          <span
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: 999,
                              background: '#f59e0b',
                              display: 'inline-block',
                            }}
                          />
                          {otherViewers.length === 1
                            ? otherViewers[0].userName
                            : `${otherViewers.length} bezig`}
                        </span>
                      ) : null}
                      <span style={{ fontSize: '0.72rem', color: '#888', marginLeft: 'auto' }}>
                        {t.received_at ? new Date(t.received_at).toLocaleString('nl-NL') : ''}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div
          style={{
            flex: 1,
            minWidth: 0,
            overflowY: isMobile ? 'visible' : 'auto',
            paddingLeft: isMobile ? 0 : '0.25rem',
            display: showListOnMobile ? 'none' : 'block',
          }}
        >
          {!detail ? (
            <p style={{ color: '#666' }}>Selecteer een ticket.</p>
          ) : (
            <div className="wc-section" style={{ padding: isMobile ? '0.75rem' : '1rem' }}>
              {isMobile ? (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setSelectedId(null)}
                  style={{
                    marginBottom: '0.75rem',
                    fontSize: '0.85rem',
                    padding: '0.35rem 0.7rem',
                  }}
                >
                  ← Terug naar lijst
                </button>
              ) : null}
              {(() => {
                const viewers = Array.isArray(presence[detail.id]) ? presence[detail.id] : [];
                const others = viewers.filter((v) => v && v.userId !== currentUserId);
                if (others.length === 0) return null;
                const names = others.map((v) => v.userName).join(', ');
                return (
                  <div
                    role="status"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '0.5rem 0.75rem',
                      marginBottom: '0.75rem',
                      background: '#fef3c7',
                      border: '1px solid #fcd34d',
                      color: '#92400e',
                      borderRadius: 8,
                      fontSize: '0.85rem',
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 999,
                        background: '#f59e0b',
                        display: 'inline-block',
                        flexShrink: 0,
                      }}
                    />
                    <span>
                      <strong>{names}</strong>{' '}
                      {others.length === 1 ? 'heeft' : 'hebben'} dit ticket op dit moment ook open —
                      let op voor dubbel werk.
                    </span>
                  </div>
                );
              })()}
              <h3 style={{ marginTop: 0, fontSize: isMobile ? '1.05rem' : '1.25rem', wordBreak: 'break-word' }}>
                {detail.subject}
              </h3>
              <p style={{ margin: '0.25rem 0', color: '#555' }}>
                <strong>Van:</strong> {detail.from_name ? `${detail.from_name} ` : ''}
                &lt;{detail.from_email}&gt;
              </p>
              <p style={{ margin: '0.25rem 0', color: '#555' }}>
                <strong>Ontvangen:</strong>{' '}
                {detail.received_at ? new Date(detail.received_at).toLocaleString('nl-NL') : '-'}
              </p>
              {detail.mailbox ? (
                <p style={{ margin: '0.25rem 0', color: '#555', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <strong>Postvak:</strong> <MailboxTag address={detail.mailbox} aliases={mailboxAliases} />
                </p>
              ) : null}
              <div style={{ margin: '0.75rem 0', display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <label style={{ fontSize: '0.85rem' }}>
                  Status{' '}
                  <select
                    value={detail.status}
                    disabled={busy}
                    onChange={(e) => setStatus(e.target.value)}
                    style={{ marginLeft: 6 }}
                  >
                    <option value="open">open</option>
                    <option value="inbehandeling">inbehandeling</option>
                    <option value="afgehandeld">afgehandeld</option>
                  </select>
                </label>
                <label style={{ fontSize: '0.85rem' }}>
                  Toegewezen aan{' '}
                  <select
                    value={detail.assigned_to || ''}
                    disabled={busy}
                    onChange={(e) => handleAssign(e.target.value)}
                    style={{ marginLeft: 6 }}
                  >
                    <option value="">— niemand —</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.gebruikersnaam}
                      </option>
                    ))}
                  </select>
                </label>
                {currentUserId && detail.assigned_to !== currentUserId ? (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ fontSize: '0.76rem', padding: '0.25rem 0.5rem' }}
                    onClick={() => handleAssign(currentUserId)}
                    disabled={busy}
                  >
                    Naar mij
                  </button>
                ) : null}
                <Badge category={detail.category || 'overig'} />
              </div>
              {detail.ai_summary ? (
                <div
                  style={{
                    marginBottom: '0.6rem',
                    fontSize: '0.85rem',
                    color: '#4b5563',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setAiSummaryOpen((v) => !v)}
                    aria-expanded={aiSummaryOpen}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      color: '#4b5563',
                      fontSize: '0.82rem',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                    title={aiSummaryOpen ? 'AI-samenvatting verbergen' : 'AI-samenvatting tonen'}
                  >
                    <span style={{ fontSize: '0.7rem' }}>{aiSummaryOpen ? '▾' : '▸'}</span>
                    <span style={{ fontWeight: 600 }}>AI-samenvatting</span>
                    {!aiSummaryOpen ? (
                      <span
                        style={{
                          color: '#6b7280',
                          fontWeight: 400,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: 520,
                          display: 'inline-block',
                        }}
                      >
                        — {detail.ai_summary}
                      </span>
                    ) : null}
                  </button>
                  {aiSummaryOpen ? (
                    <div style={{ marginTop: 4, paddingLeft: 18, lineHeight: 1.4 }}>
                      {detail.ai_summary}
                    </div>
                  ) : null}
                </div>
              ) : null}
              {/* Body-weergave: compacte segmented control + vergroten-icoon rechts.
                  Het "Weergave:"-label is weggelaten omdat de twee pill-knoppen
                  voor zichzelf spreken en een regel hoogte besparen. */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  marginBottom: '0.35rem',
                }}
              >
                <div
                  role="tablist"
                  aria-label="Weergavemodus e-mail"
                  style={{
                    display: 'inline-flex',
                    border: '1px solid #e5e7eb',
                    borderRadius: 999,
                    padding: 2,
                    background: '#f9fafb',
                  }}
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={showHtmlBody}
                    onClick={() => setShowHtmlBody(true)}
                    style={{
                      fontSize: '0.74rem',
                      padding: '0.2rem 0.6rem',
                      border: 'none',
                      borderRadius: 999,
                      cursor: 'pointer',
                      background: showHtmlBody ? '#fff' : 'transparent',
                      color: showHtmlBody ? '#111827' : '#6b7280',
                      fontWeight: showHtmlBody ? 600 : 400,
                      boxShadow: showHtmlBody ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                    }}
                  >
                    Opgemaakt
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={!showHtmlBody}
                    onClick={() => setShowHtmlBody(false)}
                    style={{
                      fontSize: '0.74rem',
                      padding: '0.2rem 0.6rem',
                      border: 'none',
                      borderRadius: 999,
                      cursor: 'pointer',
                      background: !showHtmlBody ? '#fff' : 'transparent',
                      color: !showHtmlBody ? '#111827' : '#6b7280',
                      fontWeight: !showHtmlBody ? 600 : 400,
                      boxShadow: !showHtmlBody ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                    }}
                  >
                    Platte tekst
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setShowOriginalPopup(true)}
                  title="Open de originele mail in een groot leesvenster"
                  aria-label="Vergroten"
                  style={{
                    marginLeft: 'auto',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#6b7280',
                    fontSize: '1rem',
                    padding: '0.15rem 0.35rem',
                    borderRadius: 6,
                    lineHeight: 1,
                  }}
                >
                  ⤢
                </button>
              </div>
              <div
                style={{
                  whiteSpace: showHtmlBody && detail.body_html ? 'normal' : 'pre-wrap',
                  fontSize: '0.9rem',
                  maxHeight: 220,
                  overflowY: 'auto',
                  marginBottom: '0.85rem',
                  paddingTop: '0.2rem',
                  borderTop: '1px solid #f1f5f9',
                  opacity: detailLoading ? 0.85 : 1,
                }}
              >
                {detailLoading ? (
                  <p style={{ color: '#64748b', margin: 0, fontStyle: 'italic' }}>Bericht laden…</p>
                ) : showHtmlBody && detail.body_html ? (
                  <div dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(detail.body_html) }} />
                ) : (
                  detail.body || '(leeg bericht)'
                )}
              </div>
              {detailEnriching && !detail.attachments_checked ? (
                <p style={{ color: '#94a3b8', fontSize: '0.78rem', margin: '0 0 0.5rem' }}>
                  Bijlagen en inline-plaatjes worden opgehaald…
                </p>
              ) : null}
              {(() => {
                const hasList =
                  Array.isArray(detail.attachments) && detail.attachments.length > 0;
                if (hasList) return null;
                if (detail.attachments_error) {
                  return (
                    <div
                      style={{
                        marginBottom: '0.85rem',
                        fontSize: '0.78rem',
                        color: '#b45309',
                        background: '#fffbeb',
                        border: '1px solid #fde68a',
                        borderRadius: 6,
                        padding: '0.35rem 0.55rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                      title={detail.attachments_error}
                    >
                      <span aria-hidden="true">⚠️</span>
                      <span>
                        Bijlagen konden niet uit Outlook geladen worden ({detail.attachments_error}
                        ).
                      </span>
                    </div>
                  );
                }
                if (detail.has_attachments_hint === true) {
                  return (
                    <div
                      style={{
                        marginBottom: '0.85rem',
                        fontSize: '0.78rem',
                        color: '#92400e',
                        background: '#fffbeb',
                        border: '1px solid #fde68a',
                        borderRadius: 6,
                        padding: '0.35rem 0.55rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      <span aria-hidden="true">📎</span>
                      <span>
                        Outlook meldt dat deze mail bijlagen heeft, maar ze konden niet uitgepakt
                        worden (mogelijk alleen inline afbeeldingen of een permissie-probleem).
                      </span>
                    </div>
                  );
                }
                if (!detailEnriching && detail.attachments_checked === false) {
                  return (
                    <div
                      style={{
                        marginBottom: '0.85rem',
                        fontSize: '0.78rem',
                        color: '#64748b',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        flexWrap: 'wrap',
                      }}
                    >
                      <span>Bijlagen zijn nog niet geladen.</span>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}
                        disabled={busy || detailEnriching}
                        onClick={() => {
                          const t = loadDetailTokenRef.current;
                          setDetailEnriching(true);
                          loadDetailEnrich(detail.id, t);
                        }}
                      >
                        Opnieuw laden
                      </button>
                    </div>
                  );
                }
                return null;
              })()}
              {Array.isArray(detail.attachments) && detail.attachments.length > 0 ? (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 6,
                    marginBottom: '0.85rem',
                    fontSize: '0.82rem',
                  }}
                >
                  <span style={{ color: '#6b7280', alignSelf: 'center', marginRight: 4 }}>
                    📎 {detail.attachments.length}{' '}
                    {detail.attachments.length === 1 ? 'bijlage' : 'bijlagen'}:
                  </span>
                  {detail.attachments.map((a) => {
                    const sizeKb = a.size ? Math.max(1, Math.round(a.size / 1024)) : 0;
                    const sizeLabel = sizeKb >= 1024
                      ? `${(sizeKb / 1024).toFixed(1)} MB`
                      : sizeKb > 0
                        ? `${sizeKb} KB`
                        : '';
                    const kind = attachmentKind(a.contentType, a.name);
                    const previewable = kind !== 'other';
                    const icon = kind === 'pdf' ? '📕'
                      : kind === 'image' ? '🖼️'
                        : kind === 'text' ? '📄'
                          : '📎';
                    return (
                      <span
                        key={a.id}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'stretch',
                          border: '1px solid #d1d5db',
                          borderRadius: 999,
                          background: '#f9fafb',
                          overflow: 'hidden',
                          fontSize: '0.8rem',
                          maxWidth: 360,
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => handleOpenAttachment(detail.id, a)}
                          disabled={busy}
                          title={
                            previewable
                              ? `Open ${a.name} in preview${sizeLabel ? ` (${sizeLabel})` : ''}`
                              : `Download ${a.name}${sizeLabel ? ` (${sizeLabel})` : ''}`
                          }
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '0.2rem 0.55rem',
                            border: 'none',
                            background: 'transparent',
                            color: '#111827',
                            cursor: busy ? 'wait' : 'pointer',
                            fontSize: 'inherit',
                            minWidth: 0,
                          }}
                        >
                          <span aria-hidden="true">{icon}</span>
                          <span
                            style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {a.name}
                          </span>
                          {sizeLabel ? (
                            <span style={{ color: '#6b7280', fontSize: '0.72rem' }}>
                              {sizeLabel}
                            </span>
                          ) : null}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDownloadAttachment(detail.id, a)}
                          disabled={busy}
                          title="Direct downloaden"
                          aria-label={`Download ${a.name}`}
                          style={{
                            border: 'none',
                            borderLeft: '1px solid #e5e7eb',
                            background: 'transparent',
                            color: '#6b7280',
                            cursor: busy ? 'wait' : 'pointer',
                            padding: '0 0.5rem',
                            fontSize: '0.82rem',
                          }}
                        >
                          ⬇
                        </button>
                      </span>
                    );
                  })}
                </div>
              ) : null}
              {translatedBody ? (
                <div
                  style={{
                    border: '1px solid #d1fae5',
                    background: '#ecfdf5',
                    borderRadius: 8,
                    padding: '0.7rem',
                    marginBottom: '1rem',
                    whiteSpace: translatedBodyIsHtml ? 'normal' : 'pre-wrap',
                    fontSize: '0.9rem',
                    maxHeight: 320,
                    overflowY: 'auto',
                  }}
                >
                  <strong>Vertaling ({targetLanguage}):</strong>
                  {translatedBodyIsHtml ? (
                    <div
                      style={{ marginTop: 6 }}
                      dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(translatedBody) }}
                    />
                  ) : (
                    <div style={{ marginTop: 6 }}>{translatedBody}</div>
                  )}
                </div>
              ) : null}
              {/* Conversatie-blok: inkomende + verzonden mails uit dezelfde Outlook-thread.
                  We tonen het blok ALLEEN als er meer dan één bericht is of als er
                  een fout/laad-status relevant is. Bij één enkele mail is de thread
                  identiek aan de body hierboven — dan is dit blok alleen maar ruis. */}
              {(() => {
                const hasThread = Array.isArray(thread) && thread.length > 0;
                const shouldRender =
                  (hasThread && thread.length >= 2) ||
                  threadError ||
                  (!!detail.final_reply && (!hasThread || thread.length === 0));
                if (!shouldRender) return null;
                const sentCount = hasThread ? thread.filter((m) => m.is_sent).length : 0;
                const recvCount = hasThread ? thread.length - sentCount : 0;
                const keyOf = (msg, idx) => String(msg.id || `idx-${idx}`);
                const toggleOne = (k) => {
                  setOpenMessageIds((prev) => {
                    const next = new Set(prev);
                    if (next.has(k)) next.delete(k);
                    else next.add(k);
                    return next;
                  });
                };
                const allOpen =
                  hasThread && thread.every((m, i) => openMessageIds.has(keyOf(m, i)));
                const toggleAll = () => {
                  if (!hasThread) return;
                  if (allOpen) setOpenMessageIds(new Set());
                  else setOpenMessageIds(new Set(thread.map((m, i) => keyOf(m, i))));
                };
                return (
              <div
                style={{
                  paddingTop: '0.4rem',
                  paddingBottom: '0.2rem',
                  marginBottom: '0.85rem',
                  borderTop: '1px solid #f1f5f9',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '0.5rem',
                    flexWrap: 'wrap',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setThreadExpanded((v) => !v)}
                    aria-expanded={threadExpanded}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      fontSize: '0.86rem',
                      color: '#111827',
                    }}
                    title={threadExpanded ? 'Conversatie inklappen' : 'Conversatie uitklappen'}
                  >
                    <span style={{ fontSize: '0.72rem' }}>{threadExpanded ? '▾' : '▸'}</span>
                    <strong>
                      Conversatie
                      {hasThread ? ` (${thread.length})` : ''}
                    </strong>
                    {hasThread ? (
                      <span style={{ color: '#6b7280', fontSize: '0.78rem', fontWeight: 400 }}>
                        {sentCount} verzonden · {recvCount} ontvangen
                      </span>
                    ) : null}
                  </button>
                  <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    {hasThread && threadExpanded ? (
                      <button
                        type="button"
                        onClick={toggleAll}
                        style={{
                          fontSize: '0.72rem',
                          padding: '0.15rem 0.45rem',
                          background: 'transparent',
                          border: 'none',
                          color: '#6b7280',
                          cursor: 'pointer',
                        }}
                        title={allOpen ? 'Alle berichten inklappen' : 'Alle berichten uitklappen'}
                      >
                        {allOpen ? 'Alles dicht' : 'Alles open'}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => loadThread(detail.id)}
                      disabled={threadLoading}
                      style={{
                        fontSize: '0.9rem',
                        padding: '0.1rem 0.4rem',
                        background: 'transparent',
                        border: 'none',
                        color: '#6b7280',
                        cursor: threadLoading ? 'wait' : 'pointer',
                        lineHeight: 1,
                      }}
                      title={
                        thread
                          ? 'Conversatie verversen uit Outlook'
                          : 'Conversatie ophalen uit Outlook'
                      }
                      aria-label="Ververs conversatie"
                    >
                      {threadLoading ? '…' : '↻'}
                    </button>
                  </div>
                </div>
                {threadError && threadExpanded ? (
                  <div
                    style={{
                      marginTop: '0.5rem',
                      color: '#b91c1c',
                      fontSize: '0.8rem',
                    }}
                  >
                    {threadError}
                  </div>
                ) : null}
                {threadExpanded &&
                Array.isArray(thread) &&
                thread.length === 0 &&
                !threadLoading ? (
                  <div style={{ marginTop: '0.5rem', color: '#6b7280', fontSize: '0.82rem' }}>
                    Geen berichten gevonden in deze conversatie.
                  </div>
                ) : null}
                {threadExpanded && hasThread ? (
                  <div style={{ marginTop: '0.6rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    {thread.map((msg, idx) => {
                      const when = msg.sent_at || msg.received_at || '';
                      const date = when ? new Date(when) : null;
                      const dateStr = date && !Number.isNaN(date.getTime())
                        ? date.toLocaleString('nl-NL', {
                            day: '2-digit', month: '2-digit', year: 'numeric',
                            hour: '2-digit', minute: '2-digit',
                          })
                        : '';
                      const sent = !!msg.is_sent;
                      const k = keyOf(msg, idx);
                      const isOpen = openMessageIds.has(k);
                      const preview = String(
                        msg.body_preview ||
                          msg.body ||
                          (msg.body_html ? msg.body_html.replace(/<[^>]+>/g, ' ') : '')
                      )
                        .replace(/\s+/g, ' ')
                        .trim()
                        .slice(0, 140);
                      return (
                        <div
                          key={k}
                          style={{
                            border: `1px solid ${sent ? '#bfdbfe' : '#e5e7eb'}`,
                            borderRadius: 6,
                            background: sent ? '#eff6ff' : '#ffffff',
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => toggleOne(k)}
                            aria-expanded={isOpen}
                            style={{
                              width: '100%',
                              textAlign: 'left',
                              cursor: 'pointer',
                              background: 'transparent',
                              border: 'none',
                              padding: '0.45rem 0.6rem',
                              fontSize: '0.82rem',
                              display: 'flex',
                              gap: '0.5rem',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                            }}
                          >
                            <span style={{ fontSize: '0.7rem', color: '#6b7280', width: '0.9rem' }}>
                              {isOpen ? '▾' : '▸'}
                            </span>
                            <span
                              style={{
                                fontSize: '0.68rem',
                                fontWeight: 600,
                                padding: '0.1rem 0.4rem',
                                borderRadius: 10,
                                background: sent ? '#2563eb' : '#6b7280',
                                color: 'white',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {sent ? 'Verzonden' : 'Ontvangen'}
                            </span>
                            <strong style={{ fontSize: '0.82rem' }}>
                              {sent
                                ? `Aan: ${(msg.to || []).join(', ') || '—'}`
                                : `${msg.from_name || msg.from_email || '—'}`}
                            </strong>
                            <span style={{ color: '#6b7280', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                              {dateStr}
                            </span>
                            {msg.has_attachments ? (
                              <span style={{ color: '#6b7280', fontSize: '0.78rem' }}>📎</span>
                            ) : null}
                            {!isOpen && preview ? (
                              <span
                                style={{
                                  color: '#6b7280',
                                  fontSize: '0.78rem',
                                  fontWeight: 400,
                                  flex: '1 1 200px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                — {preview}
                              </span>
                            ) : null}
                          </button>
                          {isOpen ? (
                            <div style={{ padding: '0 0.6rem 0.6rem', fontSize: '0.88rem' }}>
                              {msg.cc && msg.cc.length ? (
                                <div style={{ color: '#6b7280', fontSize: '0.75rem', marginBottom: 4 }}>
                                  CC: {msg.cc.join(', ')}
                                </div>
                              ) : null}
                              {msg.body_html ? (
                                <div
                                  style={{
                                    border: '1px solid #f3f4f6',
                                    borderRadius: 4,
                                    padding: '0.5rem',
                                    maxHeight: 320,
                                    overflow: 'auto',
                                    background: 'white',
                                  }}
                                  dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(msg.body_html) }}
                                />
                              ) : (
                                <div
                                  style={{
                                    whiteSpace: 'pre-wrap',
                                    border: '1px solid #f3f4f6',
                                    borderRadius: 4,
                                    padding: '0.5rem',
                                    maxHeight: 320,
                                    overflow: 'auto',
                                    background: 'white',
                                  }}
                                >
                                  {msg.body || msg.body_preview || '(leeg)'}
                                </div>
                              )}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
                {/* Fallback: lokaal opgeslagen antwoord laten zien als Graph nog niet
                    geladen is (of mislukt). Zo zie je altijd meteen terug wat je hebt verstuurd. */}
                {threadExpanded && (!thread || thread.length === 0) && detail.final_reply ? (
                  <div
                    style={{
                      marginTop: '0.6rem',
                      border: '1px solid #bfdbfe',
                      background: '#eff6ff',
                      borderRadius: 6,
                      padding: '0.5rem 0.6rem',
                      fontSize: '0.85rem',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        gap: '0.5rem',
                        alignItems: 'center',
                        marginBottom: 4,
                        flexWrap: 'wrap',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '0.68rem',
                          fontWeight: 600,
                          padding: '0.1rem 0.4rem',
                          borderRadius: 10,
                          background: '#2563eb',
                          color: 'white',
                        }}
                      >
                        Verzonden
                      </span>
                      {detail.replied_at ? (
                        <span style={{ color: '#6b7280', fontSize: '0.78rem' }}>
                          {new Date(detail.replied_at).toLocaleString('nl-NL')}
                        </span>
                      ) : null}
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{detail.final_reply}</div>
                  </div>
                ) : null}
              </div>
                );
              })()}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  flexWrap: 'wrap',
                  marginBottom: '0.5rem',
                }}
              >
                <h4 style={{ margin: 0 }}>Antwoord</h4>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowReplyOptions((v) => !v)}
                  aria-expanded={showReplyOptions}
                  style={{ fontSize: '0.72rem', padding: '0.22rem 0.55rem' }}
                  title="CC, BCC en bijlagen tonen"
                >
                  {showReplyOptions ? '▾ CC / BCC / bijlagen' : '▸ CC / BCC / bijlagen'}
                </button>
                {!showReplyOptions &&
                (cc.trim() || bcc.trim() || attachments.length > 0) ? (
                  <span style={{ fontSize: '0.72rem', color: '#6b7280' }}>
                    {[
                      cc.trim() ? 'CC' : null,
                      bcc.trim() ? 'BCC' : null,
                      attachments.length > 0 ? `${attachments.length} bijlage${attachments.length > 1 ? 'n' : ''}` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                ) : null}
                {/* Alle AI- en vertaalacties zitten onder één rustige "AI ▾"-knop,
                    zodat de antwoordregel niet langer vol staat met vier losse
                    knoppen. De taalkeuze verhuist mee naar het menu. */}
                <div
                  ref={aiMenuRef}
                  style={{ marginLeft: 'auto', position: 'relative' }}
                >
                  <button
                    type="button"
                    onClick={() => setAiMenuOpen((v) => !v)}
                    disabled={busy}
                    aria-haspopup="menu"
                    aria-expanded={aiMenuOpen}
                    title="AI- en vertaalacties"
                    style={{
                      fontSize: '0.74rem',
                      padding: '0.25rem 0.65rem',
                      borderRadius: 999,
                      border: '1px solid #d1d5db',
                      background: aiMenuOpen ? '#f3f4f6' : '#fff',
                      color: '#111827',
                      cursor: busy ? 'not-allowed' : 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <span role="img" aria-hidden="true">✨</span>
                    <span>AI</span>
                    <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>▾</span>
                  </button>
                  {aiMenuOpen ? (
                    <div
                      role="menu"
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        right: 0,
                        minWidth: 220,
                        background: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: 8,
                        boxShadow: '0 10px 24px rgba(0,0,0,0.08)',
                        zIndex: 20,
                        padding: '0.3rem',
                      }}
                    >
                      {[
                        { label: 'AI analyseren', onClick: handleAnalyze, icon: '🔍' },
                        { label: 'AI-antwoord voorstellen', onClick: handleSuggest, icon: '💬' },
                        { label: 'Vertaal originele e-mail', onClick: handleTranslate, icon: '🌐' },
                        { label: 'Vertaal mijn antwoord (zonder handtekening)', onClick: handleTranslateReply, icon: '🌐' },
                      ].map((item) => (
                        <button
                          key={item.label}
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setAiMenuOpen(false);
                            item.onClick();
                          }}
                          disabled={busy}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            width: '100%',
                            textAlign: 'left',
                            background: 'transparent',
                            border: 'none',
                            padding: '0.45rem 0.6rem',
                            borderRadius: 6,
                            cursor: busy ? 'not-allowed' : 'pointer',
                            fontSize: '0.82rem',
                            color: '#111827',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#f3f4f6';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                          }}
                        >
                          <span aria-hidden="true" style={{ fontSize: '0.9rem' }}>
                            {item.icon}
                          </span>
                          <span>{item.label}</span>
                        </button>
                      ))}
                      <div
                        style={{
                          borderTop: '1px solid #f1f5f9',
                          marginTop: 4,
                          paddingTop: 6,
                          paddingBottom: 2,
                          paddingLeft: '0.6rem',
                          paddingRight: '0.6rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          fontSize: '0.78rem',
                          color: '#6b7280',
                        }}
                      >
                        <span>Taal:</span>
                        <select
                          value={targetLanguage}
                          onChange={(e) => setTargetLanguage(e.target.value)}
                          style={{
                            flex: 1,
                            padding: '0.18rem 0.35rem',
                            borderRadius: 6,
                            border: '1px solid #d1d5db',
                            fontSize: '0.78rem',
                            background: '#fff',
                          }}
                        >
                          <option>Nederlands</option>
                          <option>Engels</option>
                          <option>Duits</option>
                          <option>Frans</option>
                          <option>Spaans</option>
                          <option>Italiaans</option>
                        </select>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
              {showReplyOptions ? (
                <>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '0.5rem' }}>
                    <label style={{ flex: '1 1 200px' }}>
                      CC (komma-gescheiden)
                      <input
                        style={{ width: '100%', marginTop: 4, padding: '0.4rem 0.5rem', border: '1px solid #ccc', borderRadius: 6 }}
                        value={cc}
                        onChange={(e) => setCc(e.target.value)}
                        placeholder="naam@voorbeeld.nl"
                      />
                    </label>
                    <label style={{ flex: '1 1 200px' }}>
                      BCC
                      <input
                        style={{ width: '100%', marginTop: 4, padding: '0.4rem 0.5rem', border: '1px solid #ccc', borderRadius: 6 }}
                        value={bcc}
                        onChange={(e) => setBcc(e.target.value)}
                        placeholder="optioneel"
                      />
                    </label>
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <label style={{ fontSize: '0.85rem' }}>
                      Bijlagen
                      <input type="file" multiple onChange={onPickFiles} style={{ display: 'block', marginTop: 4 }} />
                    </label>
                    {attachments.length > 0 ? (
                      <ul style={{ fontSize: '0.8rem', margin: '0.35rem 0 0 1rem' }}>
                        {attachments.map((a, i) => (
                          <li key={`${a.name}-${i}`}>
                            {a.name}{' '}
                            <button type="button" className="btn btn-secondary" style={{ padding: '0 0.35rem', fontSize: '0.75rem' }} onClick={() => setAttachments((x) => x.filter((_, j) => j !== i))}>
                              verwijder
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                </>
              ) : null}
              <div style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                <div style={{ marginBottom: 6 }}>Antwoord</div>
                <RichFormatToolbar editorRef={replyEditorRef} />
                <div
                  ref={replyEditorRef}
                  className="ticket-rich-editor ticket-reply-textarea"
                  contentEditable
                  suppressContentEditableWarning
                  role="textbox"
                  aria-multiline="true"
                  aria-label="Antwoordtekst"
                  onFocus={(e) => resetRichEditorTypingFormats(e.currentTarget)}
                  onPaste={(e) => {
                    e.preventDefault();
                    const txt = e.clipboardData.getData('text/plain') || '';
                    insertHtmlAtCaret(e.currentTarget, plainToTicketComposeHtml(txt));
                    e.currentTarget.innerHTML = sanitizeTicketComposeHtml(e.currentTarget.innerHTML);
                    resetRichEditorTypingFormats(e.currentTarget);
                  }}
                />
              </div>
              {cannedReplies.length > 0 ? (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 6,
                    padding: '0.35rem 0.5rem',
                    marginTop: 4,
                    borderTop: '1px solid #e5e7eb',
                    background: '#f9fafb',
                    borderRadius: 6,
                  }}
                >
                  <span style={{ fontSize: '0.72rem', color: '#6b7280', marginRight: 2 }}>
                    Standaard:
                  </span>
                  {cannedReplies.map((r, i) => (
                    <button
                      key={`${r.title}-${i}`}
                      type="button"
                      onClick={() => insertCannedReply(r.body)}
                      title={r.body.slice(0, 200)}
                      style={{
                        fontSize: '0.72rem',
                        padding: '0.2rem 0.55rem',
                        border: '1px solid #d1d5db',
                        borderRadius: 999,
                        background: '#fff',
                        color: '#374151',
                        cursor: 'pointer',
                        lineHeight: 1.2,
                      }}
                    >
                      {r.title}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => navigate('/tickets/instellingen')}
                    title="Standaard antwoorden beheren"
                    style={{
                      fontSize: '0.72rem',
                      padding: '0.2rem 0.5rem',
                      border: '1px dashed #d1d5db',
                      borderRadius: 999,
                      background: 'transparent',
                      color: '#6b7280',
                      cursor: 'pointer',
                      marginLeft: 'auto',
                    }}
                  >
                    Beheren…
                  </button>
                </div>
              ) : (
                <div
                  style={{
                    fontSize: '0.72rem',
                    color: '#9ca3af',
                    marginTop: 4,
                    padding: '0.25rem 0.5rem',
                  }}
                >
                  Tip: voeg via{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/tickets/instellingen')}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#00B8A9',
                      cursor: 'pointer',
                      padding: 0,
                      fontSize: 'inherit',
                      textDecoration: 'underline',
                    }}
                  >
                    instellingen
                  </button>{' '}
                  standaard antwoorden toe om ze hier snel in te voegen.
                </div>
              )}
              <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <button type="button" className="btn btn-primary" onClick={handleSend} disabled={busy}>
                  Verstuur via Outlook
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowForward((v) => !v)}
                  disabled={busy}
                  title="Stuur deze mail (inclusief bijlagen) door naar iemand anders"
                  style={{ fontSize: '0.82rem' }}
                >
                  ↪ Doorsturen
                </button>
                <span style={{ fontSize: '0.8rem', color: '#666' }}>
                  Antwoord vanuit{' '}
                  {(() => {
                    const mb = detail.mailbox || mailboxes[0];
                    if (!mb) return '(zet OUTLOOK_MAILBOXES op Railway)';
                    return mailboxAliases[mb] || mb;
                  })()}
                </span>
                <span style={{ fontSize: '0.76rem', color: '#6b7280' }}>
                  Handtekening staat al onderaan het antwoord. Aanpassen kan via het tandwiel rechtsboven.
                </span>
              </div>
              {showForward ? (
                <div
                  ref={forwardPanelRef}
                  style={{
                    marginTop: '0.6rem',
                    padding: '0.75rem 0.85rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    background: '#f9fafb',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '0.5rem',
                    }}
                  >
                    <strong style={{ fontSize: '0.9rem' }}>↪ Doorsturen</strong>
                    <button
                      type="button"
                      onClick={() => setShowForward(false)}
                      aria-label="Annuleren"
                      title="Annuleren"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#6b7280',
                        fontSize: '1rem',
                        lineHeight: 1,
                      }}
                    >
                      ×
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    <label style={{ fontSize: '0.8rem', color: '#374151' }}>
                      Aan
                      <input
                        type="text"
                        value={forwardTo}
                        onChange={(e) => setForwardTo(e.target.value)}
                        placeholder="email@voorbeeld.nl, collega@bedrijf.nl"
                        disabled={busy}
                        style={{
                          width: '100%',
                          marginTop: 4,
                          padding: '0.4rem 0.6rem',
                          border: '1px solid #d1d5db',
                          borderRadius: 6,
                          fontSize: '0.88rem',
                        }}
                      />
                    </label>
                    <label style={{ fontSize: '0.8rem', color: '#374151' }}>
                      CC (optioneel)
                      <input
                        type="text"
                        value={forwardCc}
                        onChange={(e) => setForwardCc(e.target.value)}
                        placeholder="cc@voorbeeld.nl"
                        disabled={busy}
                        style={{
                          width: '100%',
                          marginTop: 4,
                          padding: '0.4rem 0.6rem',
                          border: '1px solid #d1d5db',
                          borderRadius: 6,
                          fontSize: '0.88rem',
                        }}
                      />
                    </label>
                    <label style={{ fontSize: '0.8rem', color: '#374151' }}>
                      Opmerking boven de doorgestuurde mail (optioneel)
                      <textarea
                        rows={3}
                        value={forwardComment}
                        onChange={(e) => setForwardComment(e.target.value)}
                        placeholder="Korte toelichting voor de ontvanger…"
                        disabled={busy}
                        style={{
                          width: '100%',
                          marginTop: 4,
                          padding: '0.45rem 0.6rem',
                          border: '1px solid #d1d5db',
                          borderRadius: 6,
                          fontSize: '0.88rem',
                          fontFamily: 'inherit',
                          resize: 'vertical',
                        }}
                      />
                    </label>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleForward}
                        disabled={busy || !forwardTo.trim()}
                        style={{ fontSize: '0.85rem' }}
                      >
                        ↪ Doorsturen
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setShowForward(false)}
                        disabled={busy}
                        style={{ fontSize: '0.82rem' }}
                      >
                        Annuleren
                      </button>
                      <span style={{ fontSize: '0.76rem', color: '#6b7280' }}>
                        De originele mail en alle bijlagen worden automatisch meegestuurd.
                      </span>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>

      {attachmentPreview ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Bijlage: ${attachmentPreview.attachment?.name || ''}`}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeAttachmentPreview();
          }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(17, 24, 39, 0.6)',
            display: 'flex',
            alignItems: isMobile ? 'stretch' : 'center',
            justifyContent: 'center',
            zIndex: 1100,
            padding: isMobile ? 0 : '1rem',
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 1100,
              height: isMobile ? '100%' : '92vh',
              background: '#fff',
              borderRadius: isMobile ? 0 : 12,
              boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '0.8rem 1.1rem',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                flexWrap: 'wrap',
              }}
            >
              <strong style={{ fontSize: '0.95rem', flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                📎 {attachmentPreview.attachment?.name}
              </strong>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() =>
                  handleDownloadAttachment(attachmentPreview.ticketId, attachmentPreview.attachment)
                }
                disabled={busy}
                style={{ fontSize: '0.82rem' }}
              >
                ⬇ Downloaden
              </button>
              <button
                type="button"
                onClick={closeAttachmentPreview}
                aria-label="Sluiten"
                title="Sluiten"
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1.3rem',
                  color: '#6b7280',
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
            <div style={{ flex: 1, minHeight: 0, background: '#f3f4f6', overflow: 'auto' }}>
              {attachmentPreview.loading ? (
                <div
                  style={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6b7280',
                    fontSize: '0.9rem',
                  }}
                >
                  Bijlage laden…
                </div>
              ) : attachmentPreview.error ? (
                <div
                  style={{
                    padding: '1.5rem',
                    color: '#b91c1c',
                    fontSize: '0.9rem',
                  }}
                >
                  Preview mislukt: {attachmentPreview.error}
                </div>
              ) : attachmentPreview.kind === 'pdf' && attachmentPreview.url ? (
                <iframe
                  src={attachmentPreview.url}
                  title={attachmentPreview.attachment?.name || 'PDF preview'}
                  style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
                />
              ) : attachmentPreview.kind === 'image' && attachmentPreview.url ? (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '1rem',
                  }}
                >
                  <img
                    src={attachmentPreview.url}
                    alt={attachmentPreview.attachment?.name || ''}
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                </div>
              ) : attachmentPreview.kind === 'text' ? (
                <pre
                  style={{
                    margin: 0,
                    padding: '1rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily:
                      'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
                    fontSize: '0.82rem',
                    color: '#111827',
                    background: '#fff',
                    minHeight: '100%',
                  }}
                >
                  {attachmentPreview.text}
                </pre>
              ) : (
                <div
                  style={{
                    padding: '1.5rem',
                    color: '#6b7280',
                    fontSize: '0.9rem',
                  }}
                >
                  Geen preview beschikbaar voor dit bestandstype. Gebruik "Downloaden".
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {showOriginalPopup && detail ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Originele e-mail"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowOriginalPopup(false);
          }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(17, 24, 39, 0.6)',
            display: 'flex',
            alignItems: isMobile ? 'stretch' : 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: isMobile ? 0 : '1rem',
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 1100,
              height: isMobile ? '100%' : '92vh',
              background: '#fff',
              borderRadius: isMobile ? 0 : 12,
              boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '0.9rem 1.2rem',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                gap: '0.75rem',
                alignItems: 'flex-start',
                background: '#f9fafb',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3 style={{ margin: 0, color: '#111827', fontSize: '1.05rem', wordBreak: 'break-word' }}>
                  {detail.subject || '(zonder onderwerp)'}
                </h3>
                <div style={{ fontSize: '0.82rem', color: '#4b5563', marginTop: 4 }}>
                  <strong>Van:</strong>{' '}
                  {detail.from_name
                    ? `${detail.from_name} <${detail.from_email}>`
                    : detail.from_email || '-'}
                  {detail.received_at ? (
                    <>
                      {' · '}
                      <span title={detail.received_at}>
                        {new Date(detail.received_at).toLocaleString('nl-NL')}
                      </span>
                    </>
                  ) : null}
                  {detail.mailbox ? (
                    <>
                      {' · '}
                      <span style={{ color: '#6b7280' }}>
                        naar {mailboxAliases[detail.mailbox] || detail.mailbox}
                      </span>
                    </>
                  ) : null}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                <button
                  type="button"
                  className={showHtmlBody ? 'btn btn-primary' : 'btn btn-secondary'}
                  style={{ fontSize: '0.76rem', padding: '0.25rem 0.5rem' }}
                  onClick={() => setShowHtmlBody(true)}
                >
                  Opgemaakt
                </button>
                <button
                  type="button"
                  className={!showHtmlBody ? 'btn btn-primary' : 'btn btn-secondary'}
                  style={{ fontSize: '0.76rem', padding: '0.25rem 0.5rem' }}
                  onClick={() => setShowHtmlBody(false)}
                >
                  Platte tekst
                </button>
                <button
                  type="button"
                  onClick={() => setShowOriginalPopup(false)}
                  aria-label="Sluiten"
                  style={{
                    background: 'transparent',
                    border: 0,
                    fontSize: '1.6rem',
                    cursor: 'pointer',
                    color: '#6b7280',
                    lineHeight: 1,
                    marginLeft: '0.25rem',
                  }}
                >
                  ×
                </button>
              </div>
            </div>
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '1.2rem 1.5rem',
                fontSize: '0.95rem',
                lineHeight: 1.55,
                whiteSpace: showHtmlBody && detail.body_html ? 'normal' : 'pre-wrap',
                background: '#fff',
              }}
            >
              {showHtmlBody && detail.body_html ? (
                <div dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(detail.body_html) }} />
              ) : (
                detail.body || '(leeg bericht)'
              )}
            </div>
            <div
              style={{
                padding: '0.6rem 1.2rem',
                borderTop: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: '#f9fafb',
                fontSize: '0.78rem',
                color: '#6b7280',
              }}
            >
              <span>Druk op Esc of klik buiten het venster om te sluiten.</span>
              <button
                type="button"
                className="btn btn-secondary"
                style={{ padding: '0.25rem 0.7rem', fontSize: '0.8rem' }}
                onClick={() => setShowOriginalPopup(false)}
              >
                Sluiten
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showCompose ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Nieuwe e-mail"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !busy) setShowCompose(false);
          }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(17, 24, 39, 0.55)',
            display: 'flex',
            alignItems: isMobile ? 'stretch' : 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: isMobile ? 0 : '1rem',
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 720,
              maxHeight: isMobile ? '100%' : '90vh',
              minHeight: isMobile ? '100%' : undefined,
              overflowY: 'auto',
              background: '#fff',
              borderRadius: isMobile ? 0 : 12,
              padding: isMobile ? '1rem' : '1.25rem 1.5rem',
              boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, flex: 1, color: '#00B8A9' }}>Nieuwe e-mail</h3>
              <button
                type="button"
                onClick={() => !busy && setShowCompose(false)}
                aria-label="Sluiten"
                style={{
                  background: 'transparent',
                  border: 0,
                  fontSize: '1.4rem',
                  cursor: busy ? 'not-allowed' : 'pointer',
                  color: '#6b7280',
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>

            <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.6rem' }}>
              Vanuit postvak
              <select
                value={compose.mailbox}
                onChange={(e) => handleComposeMailboxChange(e.target.value)}
                style={{ display: 'block', marginTop: 4, padding: '0.4rem 0.5rem', border: '1px solid #ccc', borderRadius: 6, width: '100%' }}
              >
                {mailboxes.map((m) => (
                  <option key={m} value={m}>
                    {mailboxAliases[m] || m}
                  </option>
                ))}
              </select>
            </label>

            <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.6rem' }}>
              Aan (komma- of puntkomma-gescheiden)
              <input
                value={compose.to}
                onChange={(e) => setCompose((c) => ({ ...c, to: e.target.value }))}
                placeholder="klant@voorbeeld.nl"
                style={{ display: 'block', marginTop: 4, padding: '0.4rem 0.5rem', border: '1px solid #ccc', borderRadius: 6, width: '100%', boxSizing: 'border-box' }}
              />
            </label>

            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
              <label style={{ flex: '1 1 200px', fontSize: '0.85rem' }}>
                CC
                <input
                  value={compose.cc}
                  onChange={(e) => setCompose((c) => ({ ...c, cc: e.target.value }))}
                  placeholder="optioneel"
                  style={{ display: 'block', marginTop: 4, padding: '0.4rem 0.5rem', border: '1px solid #ccc', borderRadius: 6, width: '100%', boxSizing: 'border-box' }}
                />
              </label>
              <label style={{ flex: '1 1 200px', fontSize: '0.85rem' }}>
                BCC
                <input
                  value={compose.bcc}
                  onChange={(e) => setCompose((c) => ({ ...c, bcc: e.target.value }))}
                  placeholder="optioneel"
                  style={{ display: 'block', marginTop: 4, padding: '0.4rem 0.5rem', border: '1px solid #ccc', borderRadius: 6, width: '100%', boxSizing: 'border-box' }}
                />
              </label>
            </div>

            <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.6rem' }}>
              Onderwerp
              <input
                value={compose.subject}
                onChange={(e) => setCompose((c) => ({ ...c, subject: e.target.value }))}
                style={{ display: 'block', marginTop: 4, padding: '0.4rem 0.5rem', border: '1px solid #ccc', borderRadius: 6, width: '100%', boxSizing: 'border-box' }}
              />
            </label>

            <div style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.6rem' }}>
              <div style={{ marginBottom: 6 }}>Bericht</div>
              <RichFormatToolbar editorRef={composeEditorRef} />
              <div
                ref={composeEditorRef}
                className="ticket-rich-editor compose-editor"
                contentEditable
                suppressContentEditableWarning
                role="textbox"
                aria-multiline="true"
                aria-label="Berichttekst nieuwe e-mail"
                onFocus={(e) => resetRichEditorTypingFormats(e.currentTarget)}
                onPaste={(e) => {
                  e.preventDefault();
                  const txt = e.clipboardData.getData('text/plain') || '';
                  insertHtmlAtCaret(e.currentTarget, plainToTicketComposeHtml(txt));
                  e.currentTarget.innerHTML = sanitizeTicketComposeHtml(e.currentTarget.innerHTML);
                  resetRichEditorTypingFormats(e.currentTarget);
                }}
              />
            </div>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 8,
                marginBottom: '0.65rem',
                fontSize: '0.8rem',
                color: '#6b7280',
              }}
            >
              <span>Vertaal bericht (AI):</span>
              <select
                value={targetLanguage}
                onChange={(e) => setTargetLanguage(e.target.value)}
                style={{
                  padding: '0.25rem 0.45rem',
                  borderRadius: 6,
                  border: '1px solid #d1d5db',
                  fontSize: '0.8rem',
                  background: '#fff',
                }}
              >
                <option>Nederlands</option>
                <option>Engels</option>
                <option>Duits</option>
                <option>Frans</option>
                <option>Spaans</option>
                <option>Italiaans</option>
              </select>
              <button
                type="button"
                className="btn btn-secondary"
                style={{ padding: '0.25rem 0.65rem', fontSize: '0.78rem' }}
                onClick={handleTranslateCompose}
                disabled={busy}
              >
                Vertaal
              </button>
            </div>

            <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.6rem' }}>
              Bijlagen
              <input type="file" multiple onChange={onComposePickFiles} style={{ display: 'block', marginTop: 4 }} />
            </label>
            {compose.attachments.length > 0 ? (
              <ul style={{ fontSize: '0.8rem', margin: '0 0 0.5rem 1rem' }}>
                {compose.attachments.map((a, i) => (
                  <li key={`${a.name}-${i}`}>
                    {a.name}{' '}
                    <button
                      type="button"
                      className="btn btn-secondary"
                      style={{ padding: '0 0.35rem', fontSize: '0.75rem' }}
                      onClick={() =>
                        setCompose((c) => ({
                          ...c,
                          attachments: (c.attachments || []).filter((_, j) => j !== i),
                        }))
                      }
                    >
                      verwijder
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}

            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <button type="button" className="btn btn-primary" onClick={handleSendCompose} disabled={busy}>
                {busy ? 'Verzenden…' : 'Verzenden'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => !busy && setShowCompose(false)}
                disabled={busy}
              >
                Annuleren
              </button>
              <span style={{ fontSize: '0.76rem', color: '#6b7280', marginLeft: 'auto' }}>
                Handtekening wordt automatisch toegevoegd.
              </span>
            </div>
          </div>
        </div>
      ) : null}

      {ticketsKlaarModal}
    </div>
  );
}
